import { useEffect, useMemo, useRef, useState, type ReactNode } from 'react'
import { AttributionBadge, ValueStateChip } from './workspace/OverviewColumns'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import {
  Activity,
  AlertTriangle,
  Wand2,
  ArrowRight,
  BarChart3,
  Building2,
  CheckCircle2,
  Circle,
  Clock3,
  FileSpreadsheet,
  FileText,
  FolderOpen,
  Gavel,
  GitCompareArrows,
  Layers,
  LayoutDashboard,
  LineChart,
  Loader2,
  LockKeyhole,
  Plus,
  Search,
  ShieldCheck,
  Table2,
  Upload,
} from 'lucide-react'
import { Cell, Pie, PieChart, ResponsiveContainer, Tooltip } from 'recharts'

import { canEditModule } from '@/auth/modulePermission'
import { useSessionPermissions } from '@/auth/useCanEditModule'
import type { DealDocument } from '@/services/documents'
import { receivedByEmail } from './documentsModel'
import { ScreeningVerdict } from './ScreeningSummary'
import type { ScreenResults } from '@/services/ingestion'
import {
  MAX_WORKBOOK_UPLOAD_BYTES,
  underwritingWorkbooksService,
  type ComparisonFieldCandidate,
  type HeldMetric,
  type HeldMetricCandidate,
  type ComparisonRowStatus,
  type UnderwritingComparison,
  type UnderwritingComparisonRow,
  type UnderwritingWorkbookVersion,
  type WorkbookRole,
} from '@/services/underwritingWorkbooks'
import { UW_ROLLUP_KEY, uwPropertyModelsService, type DealRollup, type PropertyModel } from '@/services/uwPropertyModels'
import { invalidateLoanQueries } from '@/services/loanQueryKeys'
import {
  portfolioService,
  refreshAfterPropertyAdded,
  type AddedProperties,
  type NewPropertyInput,
} from '@/services/portfolio'
import type { LoanActivityEvent } from '@/pages/pipeline-intelligence/types'
import { useDealDossierStore, type DealDossierTab } from '@/store/dealDossierStore'
import type { RawDealFile } from '@/services/dealFile'
import type { DossierWorkspaceModel } from './workspace'
import { CalculationWorkbench } from './CalculationWorkbench'
import { ModelDealView } from './ModelDealView'
import { AssumptionSetsPanel } from './AssumptionSetsPanel'
import { AddPropertyForm, type AddPropertyRequest, type ChosenModel } from './AddPropertyForm'
import { PortfolioModelsPanel } from './PortfolioModelsPanel'
import { PortfolioRollupPanel } from './PortfolioRollupPanel'
import { UnderwritingWorkbookPanel } from './UnderwritingWorkbookPanel'
import { MODELS_NOT_AVAILABLE, UNDERWRITING_MODELS_RESOURCE, useLoanWorkbooks } from './useLoanWorkbooks'
import { buildEconomicsModel, scenarioLtvText } from './economicsModel'
import { SponsorStatementPanel, cashFlowSummaryTitle } from './EconomicsPage'
import { useDossierEdit } from './editContext'
import { EditableSlot, usePanelExpansion } from './ui'
import { catalogueValue, formatAmount, formatMultiple, formatPct, formatUnitValue } from './format'
import { loansService } from '@/services/loans'
import { LOAN_QUERY_KEYS } from '@/services/loanQueryKeys'
import { useClientProfile } from '@/hooks/useClientProfile'
import { isWritableField } from './editableFields'
import { buildSponsorArepComparison, clientSideLabel, comparisonSummary } from './sponsorArepComparison'
import { SponsorArepComparisonView } from './SponsorArepPanel'

/**
 * The tab's sub-tabs. The dashboard holds only what fits one screen at
 * 1366x768 — the Sponsor vs lender comparison, the models' status, the credit
 * decision and the open risks — and everything that used to scroll beneath it
 * has a tab of its own, so nothing was removed, only re-homed.
 */
type PageMode = 'dashboard' | 'models' | 'review' | 'statement' | 'scenarios' | 'credit' | 'documents'
type ReviewView =
  | 'rollup'
  | 'figures'
  | 'cashflow'
  | 'assumptions'
  | 'held'
  | 'comparison'
  | 'workbooks'
  | 'calculations'
  | 'workflow'

type UnderwritingPageProps = {
  loanId: string
  model: DossierWorkspaceModel
  documents: DealDocument[]
  activity: LoanActivityEvent[]
  raw?: RawDealFile
  workflow?: ReactNode
  onGoto?: (tab: DealDossierTab) => void
  /** The credit-box screen stored on the deal's intake record. */
  screen?: ScreenResults | null
}

const STATUS_STYLE: Record<string, string> = {
  draft: 'border-stone-700 text-stone-400',
  blocked: 'border-red-500/40 text-red-300',
  reviewed: 'border-sky-500/40 text-sky-300',
  promoted: 'border-emerald-500/40 text-emerald-300',
  superseded: 'border-stone-800 text-stone-500',
  rejected: 'border-red-500/40 text-red-300',
}

const COMPARISON_STYLE: Record<string, string> = {
  matching: 'border-emerald-500/30 bg-emerald-500/[0.06] text-emerald-300',
  different: 'border-amber-500/35 bg-amber-500/[0.07] text-amber-200',
  underwriter_only: 'border-sky-500/30 bg-sky-500/[0.06] text-sky-300',
  sponsor_only: 'border-violet-500/30 bg-violet-500/[0.06] text-violet-300',
  ambiguous: 'border-red-500/35 bg-red-500/[0.06] text-red-300',
}

const CHART_COLORS = {
  matching: '#4ade80',
  different: '#fbbf24',
  underwriterOnly: '#38bdf8',
  sponsorOnly: '#a78bfa',
  ambiguous: '#fb7185',
  mapped: '#86efac',
  unmapped: '#57534e',
} as const

function workbookRole(version: UnderwritingWorkbookVersion): WorkbookRole {
  return version.workbookContext?.role ?? version.workbookRole ?? 'other'
}

function humanDate(value: string | null | undefined): string {
  if (!value) return '—'
  const date = new Date(value)
  if (Number.isNaN(date.getTime())) return value
  return date.toLocaleString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  })
}

function humanSize(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`
  if (bytes < 1024 * 1024) return `${Math.round(bytes / 1024)} KB`
  return `${(bytes / 1024 / 1024).toFixed(1)} MB`
}

function errorDetail(error: unknown, fallback: string): string {
  const detail = (error as { response?: { data?: { detail?: unknown } } })?.response?.data?.detail
  return typeof detail === 'string' ? detail : fallback
}

function displayValue(value: unknown): string {
  if (value === null || value === undefined || value === '') return '—'
  if (typeof value === 'number') {
    return value.toLocaleString('en-US', { maximumFractionDigits: 4 })
  }
  if (typeof value === 'boolean') return value ? 'Yes' : 'No'
  return String(value)
}

function statusLabel(status: string): string {
  return status.replace(/_/g, ' ').replace(/\b\w/g, (letter: string) => letter.toUpperCase())
}

function candidates(
  side: UnderwritingComparisonRow['underwriter'],
  alternatives: ComparisonFieldCandidate[] = [],
): ComparisonFieldCandidate[] {
  if (alternatives.length) return alternatives
  return side ? [side] : []
}

function candidateKey(candidate: ComparisonFieldCandidate): string {
  return `${candidate.provenance.versionId}|${candidate.fieldId}`
}

function activitySummary(event: LoanActivityEvent): string | null {
  const fields = ['message', 'summary', 'filename', 'field_name', 'reason', 'note']
  for (const field of fields) {
    const value = event.details[field]
    if (typeof value === 'string' && value.trim()) return value
  }
  return null
}

function Surface({
  title,
  action,
  className = '',
  fill = false,
  children,
}: {
  title: string
  action?: ReactNode
  className?: string
  /**
   * Take the height of the grid cell it sits in, and scroll a long list
   * inside its own body rather than pushing the page. Every panel on the
   * dashboard and the sub-tabs built around the one-screen rule uses this,
   * so a row of panels shares one height and the page itself never scrolls.
   */
  fill?: boolean
  children: ReactNode
}) {
  const expand = usePanelExpansion(title)
  return (
    <section
      className={`dossier-panel min-w-0 overflow-hidden rounded-lg border border-amber-400/15 bg-black/30 ${
        fill ? 'flex min-h-0 flex-col xl:h-full' : ''
      } ${className}`}
    >
      <div
        className={`flex items-center justify-between gap-3 border-b border-stone-800/80 px-3 ${
          fill ? 'min-h-8 shrink-0 py-1.5' : 'min-h-9 py-2'
        }`}
      >
        <h3 className="min-w-0 truncate font-mono text-[9px] uppercase tracking-[0.16em] text-stone-300">
          {expand.trigger(title)}
        </h3>
        {action ? <span className="flex shrink-0 items-center gap-3">{action}</span> : null}
      </div>
      <div data-panel-body className={fill ? 'min-h-0 flex-1 overflow-y-auto px-2 py-1.5' : 'p-3'}>
        {children}
      </div>
      {expand.modal(children)}
    </section>
  )
}

function PanelLink({ label, onClick }: { label: string; onClick: () => void }) {
  return (
    <button
      type="button"
      onClick={onClick}
      className="inline-flex items-center gap-1 text-[10px] text-amber-300 transition-colors hover:text-amber-200"
    >
      {label} <ArrowRight size={11} />
    </button>
  )
}

function EmptyState({ icon, children }: { icon?: ReactNode; children: ReactNode }) {
  return (
    <div className="flex min-h-20 flex-col items-center justify-center gap-2 px-4 py-5 text-center text-[11px] leading-relaxed text-stone-500">
      {icon}
      <p className="max-w-md">{children}</p>
    </div>
  )
}

function StatusPill({ status }: { status: string }) {
  return (
    <span
      className={`rounded border px-1.5 py-0.5 text-[8px] uppercase tracking-wider ${
        STATUS_STYLE[status] ?? 'border-stone-700 text-stone-400'
      }`}
    >
      {statusLabel(status)}
    </span>
  )
}

/**
 * The metrics mapgen saw and would not place, and the cells it chose between.
 *
 * It recommends a metric only when the evidence points at exactly one cell —
 * an exact label match, a neighbouring value that reads as a number, one cell
 * left after duplicates collapse. Holding the rest is right: a wrong NOI cell
 * reads as a real number for ever.
 *
 * But holding them where nobody can see them is not. On The Chelsea five are
 * held, and four of the five are obvious to anyone looking at the sheet —
 * "NCF Stabilized" is the Net Cash Flow, "36 months" is the IO period. The
 * fifth, an underwritten expense against a trailing-twelve actual, is a real
 * underwriting decision. Both belong to a person, and until now neither had
 * anywhere to be made.
 */
function HeldMetricsPanel({
  items,
  unlocked,
  resolving,
  error,
  onResolve,
}: {
  items: HeldMetric[]
  unlocked: boolean
  /** The metric currently being placed, if any. */
  resolving: string | null
  error: string | null
  onResolve: (metric: string, candidate: HeldMetricCandidate) => void
}) {
  return (
    <div className="space-y-2">
      <p className="text-[9.5px] text-stone-500">
        The sweep read these and would not choose for you. Picking a cell adds it
        to the draft map and fills the deal from it — the map still goes through
        review before anything cites it.
      </p>
      {error && (
        <div className="rounded border border-red-500/25 bg-red-500/10 px-2 py-1 text-[9.5px] text-red-200">
          {error}
        </div>
      )}
      <div className="divide-y divide-stone-900">
        {items.map((item) => (
          <div key={item.metric} className="py-2">
            <div className="flex flex-wrap items-baseline gap-x-2">
              <span className="font-mono text-[10px] text-stone-200">{item.metric}</span>
              {item.field ? (
                <span className="text-[9px] text-stone-500">
                  would answer {item.fieldLabel}
                </span>
              ) : (
                <span className="text-[9px] text-amber-300/70">
                  no deal field holds this — placing it maps the cell only
                </span>
              )}
            </div>
            <div className="mt-1 space-y-1">
              {item.candidates.map((candidate) => {
                const id = `${candidate.sheet}!${candidate.valueCell}`
                return (
                  <div
                    key={id}
                    className="flex flex-wrap items-center justify-between gap-x-3 gap-y-1 rounded border border-stone-800/70 px-2 py-1.5"
                  >
                    <div className="flex min-w-0 flex-wrap items-baseline gap-x-2">
                      <span className="font-mono text-[9px] text-stone-500">{id}</span>
                      <span className="truncate text-[10px] text-stone-300" title={candidate.label ?? ''}>
                        {candidate.label ?? '—'}
                      </span>
                      {!candidate.exact && (
                        <span
                          title="The label contains the metric rather than matching it — the reason a person decides this one"
                          className="rounded-sm bg-amber-400/10 px-1 text-[7.5px] uppercase tracking-wider text-amber-300/80"
                        >
                          near match
                        </span>
                      )}
                    </div>
                    <div className="flex shrink-0 items-center gap-2">
                      {candidate.value != null ? (
                        <span className="font-mono text-[10px] text-sky-300">
                          {formatUnitValue(candidate.value, item.unit)}
                        </span>
                      ) : (
                        <span className="max-w-[280px] truncate text-[9px] text-stone-500" title={candidate.refusal ?? ''}>
                          {candidate.refusal}
                        </span>
                      )}
                      <button
                        type="button"
                        disabled={!unlocked || resolving != null}
                        onClick={() => onResolve(item.metric, candidate)}
                        title={
                          unlocked
                            ? `Anchor ${item.metric} to ${id}`
                            : 'Unlock the dossier to place a metric'
                        }
                        className="rounded border border-stone-700 px-1.5 py-0.5 text-[9px] text-stone-300 hover:border-amber-400/40 hover:text-amber-200 disabled:opacity-40"
                      >
                        {resolving === item.metric ? 'Placing…' : 'Use this cell'}
                      </button>
                    </div>
                  </div>
                )
              })}
            </div>
          </div>
        ))}
      </div>
    </div>
  )
}

/** What a card's model controls say while the dossier is locked: Upload and Add UW Model alike. */
const LOCKED_MODEL_TITLE = 'Unlock the dossier to upload a model'

/**
 * One side's "Add UW Model" on a single-property deal: another property, with its own model
 * on this side. Present only where the deal may gain a property here, and locked with the
 * dossier, as Upload is.
 */
type AddModelControl = {
  /** The workbooks on the deal this side may file under the new property. */
  models: UnderwritingWorkbookVersion[]
  /** What adding the property does to the deal and its own models, said before it is done. */
  note: ReactNode
  open: boolean
  /** This side's request is in flight; `progress` says which step it is on. */
  busy: boolean
  /** A request is in flight, on either side. */
  blocked: boolean
  progress: string | null
  error: string | null
  onOpen: () => void
  onClose: () => void
  onSubmit: (request: AddPropertyRequest) => void
}

function RoleModelCard({
  role,
  title,
  versions,
  selected,
  oppositeSelectedId,
  unlocked,
  assigning,
  uploading,
  error,
  onSelect,
  onUpload,
  onReview,
  onProposeMapping,
  proposing,
  addModel,
}: {
  role: 'underwriter' | 'sponsor'
  title: string
  versions: UnderwritingWorkbookVersion[]
  selected: UnderwritingWorkbookVersion | null
  oppositeSelectedId: string | null
  unlocked: boolean
  assigning: boolean
  uploading: boolean
  error: string | null
  /** Sweep this workbook for anchors and land them as a draft map. */
  onProposeMapping: () => void
  proposing: boolean
  onSelect: (id: string) => void
  onUpload: (file: File) => void
  onReview: () => void
  addModel?: AddModelControl
}) {
  const inputRef = useRef<HTMLInputElement>(null)
  // Every version but the one already holding the other role's slot. Intake
  // decides a role by sender — a firm's own model forwarded from an external
  // address lands as "sponsor" — and a wrong guess must be correctable here
  // rather than leaving the Internal dropdown with nothing to offer. Picking
  // a version that holds a different role re-designates it (dossier unlocked).
  const eligible = versions.filter((version) => version.id !== oppositeSelectedId)

  return (
    <article
      className={`rounded-md border p-2.5 ${
        selected ? 'border-amber-300/45 bg-amber-300/[0.035]' : 'border-stone-800 bg-black/25'
      }`}
    >
      <input
        ref={inputRef}
        type="file"
        accept=".xlsx,.xlsm"
        aria-label={`Upload ${title} workbook`}
        className="hidden"
        onChange={(event) => {
          const file = event.target.files?.[0]
          event.target.value = ''
          if (file) onUpload(file)
        }}
      />
      <div className="flex items-start justify-between gap-2">
        <div className="min-w-0">
          <div className="flex items-center gap-1.5">
            <FileSpreadsheet size={13} className={selected ? 'text-amber-300' : 'text-stone-600'} />
            <h4 className="text-[11px] font-semibold text-stone-200">{title}</h4>
          </div>
          <p className="mt-0.5 text-[9px] text-stone-600">
            {role === 'underwriter' ? 'Internal credit view' : 'Sponsor-submitted assumptions'}
          </p>
        </div>
        {selected ? <StatusPill status={selected.status} /> : null}
      </div>

      <select
        aria-label={`${title} version`}
        value={selected?.id ?? ''}
        disabled={assigning || eligible.length === 0}
        onChange={(event) => event.target.value && onSelect(event.target.value)}
        className="mt-2 w-full rounded border border-stone-700 bg-[#0c0c0c] px-2 py-1.5 text-[10px] text-stone-300 outline-none focus:border-amber-300/50 disabled:cursor-not-allowed disabled:opacity-60"
      >
        <option value="">No model assigned</option>
        {eligible.map((version) => (
          <option key={version.id} value={version.id} disabled={!unlocked && workbookRole(version) !== role}>
            v{version.versionNumber} · {version.filename}
            {workbookRole(version) === 'other'
              ? ' · unassigned'
              : workbookRole(version) !== role
                ? ` · currently ${workbookRole(version) === 'underwriter' ? 'internal' : 'sponsor'}`
                : ''}
          </option>
        ))}
      </select>

      {selected ? (
        <div className="mt-2 grid grid-cols-3 gap-2 border-t border-stone-800 pt-2 text-[9px]">
          <div>
            <div className="text-stone-600">Mapped</div>
            <div
              className={`mt-0.5 font-mono ${
                selected.mappedCellCount === 0 ? 'text-amber-300' : 'text-stone-300'
              }`}
            >
              {selected.mappedCellCount.toLocaleString()}/{selected.importedCellCount.toLocaleString()}
            </div>
          </div>
          <div>
            <div className="text-stone-600">Sheets</div>
            <div className="mt-0.5 font-mono text-stone-300">{selected.sheetCount}</div>
          </div>
          <div>
            <div className="text-stone-600">Size</div>
            <div className="mt-0.5 font-mono text-stone-300">{humanSize(selected.sizeBytes)}</div>
          </div>
        </div>
      ) : (
        <p className="mt-2 border-t border-stone-800 pt-2 text-[9.5px] leading-relaxed text-stone-600">
          Assign an existing unassigned version or upload a workbook for this role.
        </p>
      )}

      {selected && selected.parseStatus === 'complete' && (selected.mappedCellCount === 0 || !selected.templateId) ? (
        <div className="mt-2 rounded border border-amber-400/20 bg-amber-400/[0.04] px-2 py-1.5">
          <p className="text-[9.5px] leading-relaxed text-amber-100/90">
            {selected.mappedCellCount === 0
              ? `None of this model's ${selected.importedCellCount.toLocaleString()} cells are mapped, so nothing in it reaches the deal. `
              : `${selected.mappedCellCount.toLocaleString()} of ${selected.importedCellCount.toLocaleString()} cells are mapped but no draft map is on record. `}
            Sweep it for the metrics the engine recognizes — the result is a draft map, and it
            governs nothing until it is approved and locked.
          </p>
          <button
            type="button"
            disabled={!unlocked || proposing}
            onClick={onProposeMapping}
            title={unlocked ? 'Propose a cell map for this workbook' : 'Unlock the dossier to propose a map'}
            className="mt-1.5 inline-flex items-center gap-1 rounded border border-amber-400/30 px-2 py-1 text-[9.5px] text-amber-200 transition-colors hover:bg-amber-400/[0.09] disabled:cursor-not-allowed disabled:border-stone-800 disabled:text-stone-600"
          >
            {proposing ? <Loader2 size={10} className="animate-spin" /> : <Wand2 size={10} />}
            {proposing ? 'Scanning the model…' : selected.mappedCellCount === 0 ? 'Propose mapping' : 'Re-sweep'}
          </button>
        </div>
      ) : null}

      {error ? <p className="mt-2 text-[9.5px] text-red-300">{error}</p> : null}
      <div className="mt-2 flex items-center justify-between gap-2">
        <span className="flex flex-wrap items-center gap-2">
          <button
            type="button"
            disabled={!unlocked || uploading}
            onClick={() => inputRef.current?.click()}
            title={unlocked ? `Upload ${title}` : LOCKED_MODEL_TITLE}
            className="inline-flex items-center gap-1 rounded border border-amber-400/25 px-2 py-1 text-[9.5px] text-amber-200 transition-colors hover:bg-amber-400/[0.07] disabled:cursor-not-allowed disabled:border-stone-800 disabled:text-stone-600"
          >
            {uploading ? <Loader2 size={10} className="animate-spin" /> : unlocked ? <Upload size={10} /> : <LockKeyhole size={10} />}
            {uploading ? 'Parsing…' : 'Upload'}
          </button>
          {addModel ? (
            // Locked as Upload is: shown, and closed until the dossier is unlocked.
            <button
              type="button"
              aria-label={`Add UW Model (${title})`}
              aria-expanded={addModel.open}
              disabled={!unlocked || addModel.blocked}
              onClick={addModel.open ? addModel.onClose : addModel.onOpen}
              title={unlocked ? `Add another property to this deal, with its own ${title.replace(/ Model$/, '')} model` : LOCKED_MODEL_TITLE}
              className="inline-flex items-center gap-1 rounded border border-amber-400/30 px-2.5 py-1 text-[10px] font-medium text-amber-200 transition-colors hover:bg-amber-400/[0.09] disabled:cursor-not-allowed disabled:border-stone-800 disabled:text-stone-600"
            >
              {addModel.busy ? (
                <Loader2 size={11} className="animate-spin" aria-hidden />
              ) : unlocked ? (
                <Plus size={11} aria-hidden />
              ) : (
                <LockKeyhole size={11} aria-hidden />
              )}
              Add UW Model
            </button>
          ) : null}
        </span>
        {selected ? <PanelLink label="Review model" onClick={onReview} /> : null}
      </div>
      {addModel?.open ? (
        <AddPropertyForm
          label={`Add a property (${title})`}
          heading={`Another property, with its own ${title.replace(/ Model$/, '')} model`}
          models={addModel.models}
          note={addModel.note}
          busy={addModel.busy}
          progress={addModel.progress}
          error={addModel.error}
          onSubmit={addModel.onSubmit}
          onCancel={addModel.onClose}
        />
      ) : null}
    </article>
  )
}

function ComparisonSummaryChart({
  comparison,
  coverage,
  emptyText = 'Assign or upload a model to see how much of the deal it states.',
}: {
  comparison: UnderwritingComparison | undefined
  /** The focused model's read of the deal: how many deal fields it states. */
  coverage: { fieldsStated: number; fieldsApplicable: number; cellsMapped: number; filename: string } | null
  /** What to say when there is nothing to chart yet. */
  emptyText?: string
}) {
  // A model none of whose cells are mapped has not been read at all. Its
  // fields are not "not stated" — nobody knows yet what it states.
  const unread = coverage != null && coverage.cellsMapped === 0
  // With both models the picture is the comparison. With one — the usual
  // case, since intake stamps only the sponsor's role — it is how much of the
  // deal record that model actually states. Cell counts are an implementation
  // detail no underwriter reads a deal by.
  const chart = comparison
    ? [
        { name: 'Matching', value: comparison.summary.matching, color: CHART_COLORS.matching },
        { name: 'Different', value: comparison.summary.different, color: CHART_COLORS.different },
        { name: 'Internal only', value: comparison.summary.underwriterOnly, color: CHART_COLORS.underwriterOnly },
        { name: 'Sponsor only', value: comparison.summary.sponsorOnly, color: CHART_COLORS.sponsorOnly },
        { name: 'Ambiguous', value: comparison.summary.ambiguous, color: CHART_COLORS.ambiguous },
      ].filter((item) => item.value > 0)
    : coverage
      ? [
          { name: 'Stated by the model', value: coverage.fieldsStated, color: CHART_COLORS.mapped },
          {
            name: unread ? 'In unread model' : 'Not stated',
            value: Math.max(0, coverage.fieldsApplicable - coverage.fieldsStated),
            color: unread ? CHART_COLORS.different : CHART_COLORS.unmapped,
          },
        ].filter((item) => item.value > 0)
      : []
  const total = comparison?.summary.total ?? coverage?.fieldsApplicable ?? 0
  const headline = comparison ? 'Compared fields' : 'Deal fields'

  if (chart.length === 0) {
    return (
      <EmptyState icon={<BarChart3 size={18} className="text-stone-700" />}>
        {comparison
          ? 'Both models are assigned, but no mapped semantic fields are available for comparison.'
          : emptyText}
      </EmptyState>
    )
  }

  return (
    <div>
      <div className="grid grid-cols-[116px_minmax(0,1fr)] items-center gap-3">
        <div className="relative h-[116px] w-[116px]">
          <ResponsiveContainer width="100%" height="100%">
            <PieChart>
              <Pie
                data={chart}
                dataKey="value"
                nameKey="name"
                innerRadius="70%"
                outerRadius="94%"
                stroke="transparent"
                isAnimationActive={false}
              >
                {chart.map((item) => <Cell key={item.name} fill={item.color} />)}
              </Pie>
              <Tooltip
                contentStyle={{ background: '#111', border: '1px solid #44403c', fontSize: 10 }}
                itemStyle={{ color: '#e7e5e4' }}
              />
            </PieChart>
          </ResponsiveContainer>
          <div className="pointer-events-none absolute inset-0 flex flex-col items-center justify-center">
            <span className="font-mono text-xl text-stone-100">{total.toLocaleString()}</span>
            <span className="text-[8px] uppercase tracking-wide text-stone-600">{headline}</span>
          </div>
        </div>
        <div className="space-y-1.5">
          {chart.map((item) => (
            <div key={item.name} className="flex items-center justify-between gap-3 text-[9.5px]">
              <span className="flex min-w-0 items-center gap-1.5 text-stone-400">
                <Circle size={7} fill={item.color} stroke={item.color} />
                <span className="truncate">{item.name}</span>
              </span>
              <span className="font-mono text-stone-200">{item.value.toLocaleString()}</span>
            </div>
          ))}
        </div>
      </div>
      {comparison ? (
        <div className="mt-2 flex items-center justify-between border-t border-stone-800 pt-2 text-[9.5px] text-stone-500">
          <span>{comparison.summary.governed.toLocaleString()} governed decisions</span>
          <span>{comparison.summary.total ? Math.round((comparison.summary.matching / comparison.summary.total) * 100) : 0}% aligned</span>
        </div>
      ) : null}
    </div>
  )
}

function ModelPair({
  versions,
  underwriter,
  sponsor,
  unlocked,
  assigning,
  uploadingRole,
  uploadErrors,
  onSelect,
  onUpload,
  onReview,
  onProposeMapping,
  proposingRole,
  addModel,
}: {
  versions: UnderwritingWorkbookVersion[]
  underwriter: UnderwritingWorkbookVersion | null
  sponsor: UnderwritingWorkbookVersion | null
  unlocked: boolean
  assigning: boolean
  uploadingRole: WorkbookRole | null
  uploadErrors: Partial<Record<WorkbookRole, string>>
  onSelect: (role: 'underwriter' | 'sponsor', id: string) => void
  onUpload: (role: 'underwriter' | 'sponsor', file: File) => void
  onReview: () => void
  onProposeMapping: (versionId: string) => void
  proposingRole: 'underwriter' | 'sponsor' | null
  /** Each side's "Add UW Model", where the deal may gain a property here. */
  addModel?: (role: 'underwriter' | 'sponsor') => AddModelControl
}) {
  return (
    <div className="grid gap-2 md:grid-cols-2">
      <RoleModelCard
        role="underwriter"
        title="Internal Underwriting"
        versions={versions}
        selected={underwriter}
        oppositeSelectedId={sponsor?.id ?? null}
        unlocked={unlocked}
        assigning={assigning}
        uploading={uploadingRole === 'underwriter'}
        error={uploadErrors.underwriter ?? null}
        onSelect={(id) => onSelect('underwriter', id)}
        onUpload={(file) => onUpload('underwriter', file)}
        onReview={onReview}
        onProposeMapping={() => underwriter && onProposeMapping(underwriter.id)}
        proposing={proposingRole === 'underwriter'}
        addModel={addModel?.('underwriter')}
      />
      <RoleModelCard
        role="sponsor"
        title="Sponsor Model"
        versions={versions}
        selected={sponsor}
        oppositeSelectedId={underwriter?.id ?? null}
        unlocked={unlocked}
        assigning={assigning}
        uploading={uploadingRole === 'sponsor'}
        error={uploadErrors.sponsor ?? null}
        onSelect={(id) => onSelect('sponsor', id)}
        onUpload={(file) => onUpload('sponsor', file)}
        onReview={onReview}
        onProposeMapping={() => sponsor && onProposeMapping(sponsor.id)}
        proposing={proposingRole === 'sponsor'}
        addModel={addModel?.('sponsor')}
      />
    </div>
  )
}

function WorkbookEvidence({
  underwriter,
  sponsor,
  comparison,
  model,
}: {
  underwriter: UnderwritingWorkbookVersion | null
  sponsor: UnderwritingWorkbookVersion | null
  comparison: UnderwritingComparison | undefined
  model: DossierWorkspaceModel
}) {
  const evidence = [
    underwriter
      ? `${underwriter.mappedCellCount.toLocaleString()} of ${underwriter.importedCellCount.toLocaleString()} internal cells mapped`
      : null,
    sponsor
      ? `${sponsor.mappedCellCount.toLocaleString()} of ${sponsor.importedCellCount.toLocaleString()} sponsor cells mapped`
      : null,
    comparison ? `${comparison.summary.matching.toLocaleString()} normalized fields agree across both models` : null,
  ].filter((value): value is string => Boolean(value))

  const flags = [
    ...(underwriter?.blockerReasons ?? []),
    ...(sponsor?.blockerReasons ?? []),
    ...model.exceptions.map((item) => item.label),
  ]

  return (
    <div className="space-y-3">
      <div>
        <div className="mb-1.5 text-[9px] font-medium text-emerald-300">Recorded evidence</div>
        {evidence.length ? (
          <ul className="space-y-1.5">
            {evidence.slice(0, 4).map((item) => (
              <li key={item} className="flex gap-1.5 text-[9.5px] leading-relaxed text-stone-400">
                <CheckCircle2 size={10} className="mt-0.5 shrink-0 text-emerald-400" /> {item}
              </li>
            ))}
          </ul>
        ) : (
          <p className="text-[9.5px] text-stone-600">No workbook evidence has been recorded.</p>
        )}
      </div>
      <div>
        <div className="mb-1.5 text-[9px] font-medium text-amber-300">Review flags</div>
        {flags.length ? (
          <ul className="space-y-1.5">
            {flags.slice(0, 5).map((item, index) => (
              <li key={`${item}-${index}`} className="flex gap-1.5 text-[9.5px] leading-relaxed text-stone-400">
                <AlertTriangle size={10} className="mt-0.5 shrink-0 text-amber-400" /> {item}
              </li>
            ))}
          </ul>
        ) : (
          <p className="text-[9.5px] text-stone-600">No workbook blockers or deal exceptions are on record.</p>
        )}
      </div>
    </div>
  )
}

type ChecklistItem = { label: string; complete: boolean; detail: string }

/**
 * The single-deal checklist's steps. One list, read by the full checklist on
 * the Models tab and by the dashboard's compact strip, so the two can never
 * count differently.
 */
function reviewChecklistItems(
  underwriter: UnderwritingWorkbookVersion | null,
  sponsor: UnderwritingWorkbookVersion | null,
  comparison: UnderwritingComparison | undefined,
): ChecklistItem[] {
  return [
    { label: 'Internal model assigned', complete: Boolean(underwriter), detail: underwriter?.filename ?? 'Required' },
    { label: 'Sponsor model assigned', complete: Boolean(sponsor), detail: sponsor?.filename ?? 'Required for comparison' },
    {
      label: 'Internal field mapping reviewed',
      complete: Boolean(underwriter?.mappingReviewed),
      detail: underwriter?.mappingStatus ?? 'Waiting for internal model',
    },
    {
      label: 'Calculation sign-off recorded',
      complete: Boolean(underwriter?.calculationSignedOff),
      detail: underwriter?.reviewedBy ?? 'No reviewer recorded',
    },
    {
      label: 'Model comparison available',
      complete: Boolean(comparison),
      detail: comparison ? `${comparison.summary.total.toLocaleString()} normalized fields` : 'Both model roles are required',
    },
  ]
}

/** A portfolio's checklist steps: every property has a model on each side. */
function portfolioChecklistItems(rollup: DealRollup): ChecklistItem[] {
  // The client's own model first, as the single-deal checklist lists the internal model first.
  return [...rollup.sides].sort((a, b) => Number(b.role === 'underwriter') - Number(a.role === 'underwriter')).map((side) => ({
    label: `${side.label} model on every property`,
    complete: side.coverage.of > 0 && side.coverage.have === side.coverage.of,
    detail: side.missing.length
      ? `${side.coverage.have} of ${side.coverage.of} · ${side.missing.map((gap) => gap.name).join(', ')} missing`
      : `${side.coverage.have} of ${side.coverage.of} properties`,
  }))
}

function ReviewChecklist({
  underwriter,
  sponsor,
  comparison,
}: {
  underwriter: UnderwritingWorkbookVersion | null
  sponsor: UnderwritingWorkbookVersion | null
  comparison: UnderwritingComparison | undefined
}) {
  const items = reviewChecklistItems(underwriter, sponsor, comparison)
  const completed = items.filter((item) => item.complete).length

  return (
    <div>
      <div className="mb-2 flex items-center gap-3">
        <div className="h-1 flex-1 overflow-hidden rounded bg-stone-800">
          <div className="h-full bg-emerald-500" style={{ width: `${(completed / items.length) * 100}%` }} />
        </div>
        <span className="font-mono text-[9px] text-stone-500">{completed} of {items.length} complete</span>
      </div>
      <div className="divide-y divide-stone-900">
        {items.map((item) => (
          <div key={item.label} className="grid grid-cols-[16px_minmax(0,1fr)_minmax(90px,0.7fr)] items-center gap-2 py-1 text-[9.5px]">
            {item.complete ? <CheckCircle2 size={11} className="text-emerald-400" /> : <Clock3 size={11} className="text-amber-400" />}
            <span className="text-stone-300">{item.label}</span>
            <span className="truncate text-right text-stone-600" title={item.detail}>{item.detail}</span>
          </div>
        ))}
      </div>
    </div>
  )
}

/**
 * A portfolio's checklist. Its models are its properties', so the question is
 * not whether the deal has an internal and a sponsor model but whether every
 * property has one on each side. Built from the roll-up's own coverage count,
 * so the two can never disagree.
 */
function PortfolioChecklist({ rollup, failed }: { rollup: DealRollup | undefined; failed: boolean }) {
  if (!rollup) {
    return <EmptyState>{failed ? 'The property models could not be read.' : 'Reading the property models…'}</EmptyState>
  }
  const items = portfolioChecklistItems(rollup)
  const completed = items.filter((item) => item.complete).length

  return (
    <div>
      <div className="mb-2 flex items-center gap-3">
        <div className="h-1 flex-1 overflow-hidden rounded bg-stone-800">
          <div className="h-full bg-emerald-500" style={{ width: `${(completed / Math.max(items.length, 1)) * 100}%` }} />
        </div>
        <span className="font-mono text-[9px] text-stone-500">{completed} of {items.length} complete</span>
      </div>
      <div className="divide-y divide-stone-900">
        {items.map((item) => (
          <div key={item.label} className="grid grid-cols-[16px_minmax(0,1fr)_minmax(90px,0.7fr)] items-center gap-2 py-1 text-[9.5px]">
            {item.complete ? <CheckCircle2 size={11} className="text-emerald-400" /> : <Clock3 size={11} className="text-amber-400" />}
            <span className="text-stone-300">{item.label}</span>
            <span className="truncate text-right text-stone-600" title={item.detail}>{item.detail}</span>
          </div>
        ))}
      </div>
    </div>
  )
}

function ComparisonField({
  side,
  alternatives,
  label,
}: {
  side: UnderwritingComparisonRow['underwriter']
  alternatives: ComparisonFieldCandidate[]
  label: string
}) {
  const items = candidates(side, alternatives)
  if (!items.length) return <span className="text-stone-700">Not supplied</span>
  return (
    <div className="space-y-1">
      {items.map((item) => (
        <div key={candidateKey(item)} className="min-w-0">
          <div className="flex items-baseline justify-between gap-2">
            <span className="truncate text-[10px] font-medium text-stone-200" title={item.label ?? label}>{item.label || label}</span>
            <span className="shrink-0 font-mono text-[10px] text-stone-100">{displayValue(item.value)}</span>
          </div>
          <div className="mt-0.5 truncate font-mono text-[8px] text-stone-600" title={`${item.provenance.filename} · ${item.sheetName}!${item.cellRef}`}>
            {item.provenance.filename} · {item.sheetName}!{item.cellRef}
          </div>
          {item.formula ? <div className="truncate font-mono text-[8px] text-amber-300/55" title={item.formula}>{item.formula}</div> : null}
        </div>
      ))}
      {items.length > 1 ? <div className="text-[8px] text-red-300">{items.length} mapped candidates require review</div> : null}
    </div>
  )
}

function ComparisonWorkspace({
  comparison,
  loading,
  error,
  reason,
  onReasonChange,
  savingSemanticKey,
  saveError,
  onGovern,
}: {
  comparison: UnderwritingComparison | undefined
  loading: boolean
  error: unknown
  reason: string
  onReasonChange: (value: string) => void
  savingSemanticKey: string | null
  saveError: string | null
  onGovern: (row: UnderwritingComparisonRow, candidate: ComparisonFieldCandidate) => void
}) {
  const [search, setSearch] = useState('')
  const [status, setStatus] = useState<'all' | ComparisonRowStatus>('all')
  const [drafts, setDrafts] = useState<Record<string, string>>({})

  const rows = useMemo(() => {
    if (!comparison) return []
    const query = search.trim().toLowerCase()
    return comparison.rows.filter((row) => {
      if (status !== 'all' && row.status !== status) return false
      if (!query) return true
      const values = [
        row.semanticKey,
        ...candidates(row.underwriter, row.underwriterCandidates).flatMap((field) => [field.label, field.value, field.sheetName, field.cellRef]),
        ...candidates(row.sponsor, row.sponsorCandidates).flatMap((field) => [field.label, field.value, field.sheetName, field.cellRef]),
      ]
      return values.some((value) => String(value ?? '').toLowerCase().includes(query))
    })
  }, [comparison, search, status])

  if (loading) {
    return <EmptyState icon={<Loader2 size={17} className="animate-spin text-amber-300" />}>Building the normalized comparison…</EmptyState>
  }
  if (error) {
    return <EmptyState icon={<AlertTriangle size={17} className="text-red-300" />}>{errorDetail(error, 'The normalized comparison is unavailable.')}</EmptyState>
  }
  if (!comparison) {
    return <EmptyState icon={<GitCompareArrows size={18} className="text-stone-700" />}>Select one internal underwriting version and one sponsor version above to start model review.</EmptyState>
  }
  if (comparison.summary.total === 0) {
    return <EmptyState icon={<GitCompareArrows size={18} className="text-stone-700" />}>The selected workbooks have no mapped semantic fields. Assign locked model templates before choosing governing values.</EmptyState>
  }

  return (
    <div className="space-y-2">
      <div className="grid gap-2 lg:grid-cols-[minmax(220px,1fr)_170px_minmax(240px,1.2fr)]">
        <label className="relative min-w-0">
          <Search size={11} className="pointer-events-none absolute left-2 top-1/2 -translate-y-1/2 text-stone-600" />
          <input
            value={search}
            onChange={(event) => setSearch(event.target.value)}
            placeholder="Search semantic key, label, value or source…"
            className="w-full rounded border border-stone-700 bg-black/40 py-1.5 pl-6 pr-2 text-[10px] text-stone-200 outline-none placeholder:text-stone-600 focus:border-amber-300/50"
          />
        </label>
        <select
          aria-label="Comparison status"
          value={status}
          onChange={(event) => setStatus(event.target.value as 'all' | ComparisonRowStatus)}
          className="rounded border border-stone-700 bg-black/40 px-2 py-1.5 text-[10px] text-stone-300 outline-none focus:border-amber-300/50"
        >
          <option value="all">All statuses</option>
          <option value="different">Different</option>
          <option value="matching">Matching</option>
          <option value="ambiguous">Ambiguous</option>
          <option value="underwriter_only">Internal only</option>
          <option value="sponsor_only">Sponsor only</option>
        </select>
        <input
          aria-label="Governance reason"
          value={reason}
          onChange={(event) => onReasonChange(event.target.value)}
          placeholder="Decision rationale required to save a governing value"
          className="rounded border border-stone-700 bg-black/40 px-2 py-1.5 text-[10px] text-stone-200 outline-none placeholder:text-stone-600 focus:border-amber-300/50"
        />
      </div>

      {saveError ? <p className="text-[10px] text-red-300">{saveError}</p> : null}

      <div data-panel-scroll className="max-h-[520px] overflow-auto rounded border border-stone-800">
        <table className="w-full min-w-[980px] table-fixed border-collapse text-left">
          <thead className="sticky top-0 z-10 bg-[#11100f]">
            <tr className="font-mono text-[8px] uppercase tracking-wider text-stone-500">
              <th className="w-[19%] border-b border-stone-700 px-2 py-2 font-normal">Normalized field</th>
              <th className="w-[25%] border-b border-stone-700 px-2 py-2 font-normal">Internal underwriting</th>
              <th className="w-[25%] border-b border-stone-700 px-2 py-2 font-normal">Sponsor model</th>
              <th className="w-[10%] border-b border-stone-700 px-2 py-2 font-normal">Integrity</th>
              <th className="w-[21%] border-b border-stone-700 px-2 py-2 font-normal">Governing source</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((row) => {
              const allCandidates = [
                ...candidates(row.underwriter, row.underwriterCandidates),
                ...candidates(row.sponsor, row.sponsorCandidates),
              ]
              const existingDecision = row.governingDecision
              const existingKey = existingDecision
                ? `${existingDecision.governingVersionId}|${existingDecision.governingFieldId ?? ''}`
                : ''
              const selectedKey = drafts[row.semanticKey] ?? existingKey
              const selectedCandidate = allCandidates.find((candidate) => candidateKey(candidate) === selectedKey)
              const requiresDecision = row.status !== 'matching' || Boolean(existingDecision)
              return (
                <tr key={row.semanticKey} className="align-top hover:bg-amber-400/[0.025]">
                  <td className="border-b border-stone-900 px-2 py-2">
                    <div className="break-words font-mono text-[9px] text-amber-100/80">{row.semanticKey}</div>
                    {existingDecision ? (
                      <div className="mt-1 flex items-center gap-1 text-[8px] text-emerald-300">
                        <ShieldCheck size={9} /> Governed
                      </div>
                    ) : null}
                  </td>
                  <td className="border-b border-stone-900 px-2 py-2"><ComparisonField side={row.underwriter} alternatives={row.underwriterCandidates} label={row.semanticKey} /></td>
                  <td className="border-b border-stone-900 px-2 py-2"><ComparisonField side={row.sponsor} alternatives={row.sponsorCandidates} label={row.semanticKey} /></td>
                  <td className="border-b border-stone-900 px-2 py-2">
                    <span className={`inline-block rounded border px-1.5 py-0.5 text-[7.5px] uppercase tracking-wide ${COMPARISON_STYLE[row.status] ?? COMPARISON_STYLE.different}`}>
                      {statusLabel(row.status)}
                    </span>
                  </td>
                  <td className="border-b border-stone-900 px-2 py-2">
                    {requiresDecision && allCandidates.length ? (
                      <div className="space-y-1.5">
                        <select
                          aria-label={`Governing value for ${row.semanticKey}`}
                          value={selectedKey}
                          onChange={(event) => setDrafts((current) => ({ ...current, [row.semanticKey]: event.target.value }))}
                          className="w-full rounded border border-stone-700 bg-black/50 px-1.5 py-1 text-[9px] text-stone-300 outline-none focus:border-amber-300/50"
                        >
                          <option value="">Select governing source</option>
                          {allCandidates.map((candidate) => (
                            <option key={candidateKey(candidate)} value={candidateKey(candidate)}>
                              {candidate.provenance.role === 'underwriter' ? 'Internal' : 'Sponsor'} · {candidate.sheetName}!{candidate.cellRef} · {displayValue(candidate.value)}
                            </option>
                          ))}
                        </select>
                        <button
                          type="button"
                          disabled={!selectedCandidate || !reason.trim() || savingSemanticKey === row.semanticKey}
                          onClick={() => selectedCandidate && onGovern(row, selectedCandidate)}
                          className="inline-flex items-center gap-1 rounded border border-emerald-500/30 px-1.5 py-1 text-[8.5px] text-emerald-300 transition-colors hover:bg-emerald-500/[0.07] disabled:cursor-not-allowed disabled:border-stone-800 disabled:text-stone-700"
                        >
                          {savingSemanticKey === row.semanticKey ? <Loader2 size={9} className="animate-spin" /> : <ShieldCheck size={9} />}
                          {existingDecision ? 'Update decision' : 'Save decision'}
                        </button>
                        {existingDecision ? <div className="line-clamp-2 text-[8px] leading-relaxed text-stone-600" title={existingDecision.reason}>{existingDecision.reason}</div> : null}
                      </div>
                    ) : (
                      <span className="text-[8.5px] text-stone-600">Values agree</span>
                    )}
                  </td>
                </tr>
              )
            })}
          </tbody>
        </table>
        {rows.length === 0 ? <EmptyState>No normalized fields match the current filters.</EmptyState> : null}
      </div>
    </div>
  )
}

/** A scenario figure with the basis it was stated on: a stabilized one says
 *  so, and a bare one says its basis was not stated — never borrowed into the
 *  stabilized column (C09). */
function ScenarioBasisFigure({
  stabilized,
  unqualified,
  format,
}: {
  stabilized: number | undefined
  unqualified: number | undefined
  format: (value: number | undefined) => string
}) {
  const value = stabilized ?? unqualified
  if (value == null) return <>{format(undefined)}</>
  const stated = stabilized != null
  return (
    <span title={stated ? 'Stabilized, as the scenario states it' : 'The scenario does not say whether this is in-place or stabilized'}>
      {format(value)}
      <span className="ml-1 text-[7px] uppercase tracking-wide text-stone-500">{stated ? 'stab.' : 'basis n/s'}</span>
    </span>
  )
}

/** The deal's stored scenarios, one row each. Nothing is derived here: a
 *  scenario the record does not hold is not listed. */
function ScenarioTable({ economics }: { economics: ReturnType<typeof buildEconomicsModel> | null }) {
  const rows = economics
    ? (['base', 'downside', 'upside'] as const)
        .map((id) => economics.scenarios[id])
        .filter((scenario): scenario is NonNullable<typeof scenario> => Boolean(scenario))
    : []
  if (!rows.length) return <EmptyState>No underwriting scenarios are stored on this deal.</EmptyState>
  return (
    <div>
      <table className="w-full text-left text-[9.5px]">
        <thead className="font-mono text-[7.5px] uppercase tracking-wider text-stone-600">
          <tr>
            <th className="pb-1 font-normal">Scenario</th>
            <th className="pb-1 text-right font-normal">NOI</th>
            <th className="pb-1 text-right font-normal">DSCR</th>
            <th className="pb-1 text-right font-normal">LTV</th>
            <th className="pb-1 text-right font-normal">Cash on cash</th>
            <th className="pb-1 text-right font-normal">IRR</th>
          </tr>
        </thead>
        <tbody className="divide-y divide-stone-900">
          {rows.map((scenario) => (
            <tr key={scenario.id}>
              <td className="py-1.5 text-stone-300">{scenario.label}</td>
              <td className="py-1.5 text-right font-mono text-stone-200">
                <ScenarioBasisFigure
                  stabilized={scenario.noi}
                  unqualified={scenario.noiUnqualified}
                  format={formatAmount}
                />
              </td>
              <td className={`py-1.5 text-right font-mono ${(scenario.dscr ?? scenario.dscrUnqualified ?? Infinity) < 1 ? 'text-red-300' : 'text-stone-200'}`}>
                <ScenarioBasisFigure
                  stabilized={scenario.dscr}
                  unqualified={scenario.dscrUnqualified}
                  format={formatMultiple}
                />
              </td>
              <td className="py-1.5 text-right font-mono text-stone-200">{scenarioLtvText(scenario)}</td>
              <td className="py-1.5 text-right font-mono text-stone-200">{formatPct(scenario.cashOnCash)}</td>
              <td className="py-1.5 text-right font-mono text-stone-200">{formatPct(scenario.irr)}</td>
            </tr>
          ))}
        </tbody>
      </table>
      {economics?.scenarioDisclosure ? (
        <p className="mt-1.5 text-[8px] leading-snug text-stone-600">{economics.scenarioDisclosure}</p>
      ) : null}
    </div>
  )
}

/** A checklist's progress as one line: the next open step, the bar, the count. */
function ChecklistStrip({ items }: { items: ChecklistItem[] }) {
  const completed = items.filter((item) => item.complete).length
  const next = items.find((item) => !item.complete)
  return (
    <div data-testid="checklist-strip" className="grid grid-cols-[64px_minmax(0,1fr)_72px_auto] items-center gap-2 text-[10px]">
      <span className="font-mono text-[8px] uppercase tracking-wider text-stone-500">Checklist</span>
      <span className="truncate text-stone-400" title={next ? `${next.label} — ${next.detail}` : undefined}>
        {next ? (
          <>
            <Clock3 size={9} className="mr-1 inline text-amber-400" aria-hidden />
            Next: <span className="text-stone-200">{next.label}</span>
          </>
        ) : (
          <>
            <CheckCircle2 size={9} className="mr-1 inline text-emerald-400" aria-hidden />
            Every step is recorded
          </>
        )}
      </span>
      <span className="h-1 overflow-hidden rounded bg-stone-800">
        <span className="block h-full bg-emerald-500" style={{ width: `${(completed / Math.max(items.length, 1)) * 100}%` }} />
      </span>
      <span className="shrink-0 font-mono text-[9px] text-stone-500">
        {completed} of {items.length}
      </span>
    </div>
  )
}

/**
 * One model role on the dashboard's status panel: which file, its state, how
 * much of it is mapped.
 *
 * The underwriter-role workbook is called "Internal" here, as everywhere else
 * on the tab — the "Internal Underwriting" card, the "Internal model assigned"
 * step, the Model Figures toggle. It is not named after the client's column:
 * that column is broader than the model (a person's decision on the deal goes
 * there too), and one role read under two names on one tab.
 */
function ModelStatusLine({ label, version, empty }: { label: string; version: UnderwritingWorkbookVersion | null; empty: string }) {
  return (
    <div className="grid grid-cols-[64px_minmax(0,1fr)_auto] items-center gap-2 text-[10px]">
      <span className="font-mono text-[8px] uppercase tracking-wider text-stone-500">{label}</span>
      <span className={`truncate ${version ? 'text-stone-200' : 'text-stone-600'}`} title={version?.filename ?? empty}>
        {version ? version.filename : empty}
      </span>
      {version ? (
        <span className="flex shrink-0 items-center gap-1.5">
          <StatusPill status={version.status} />
          <span
            className={`font-mono text-[9px] ${version.mappedCellCount === 0 ? 'text-amber-300' : 'text-stone-400'}`}
            title={`${version.mappedCellCount.toLocaleString()} of ${version.importedCellCount.toLocaleString()} cells mapped`}
          >
            {version.mappedCellCount.toLocaleString()}/{version.importedCellCount.toLocaleString()}
          </span>
        </span>
      ) : (
        <span />
      )}
    </div>
  )
}

/** The committee decision on record, or the absence of one, on one line. */
function DecisionLine({ decision, full = false }: { decision: DossierWorkspaceModel['decision']; full?: boolean }) {
  if (!decision) return <p className="text-[9.5px] text-stone-500">No committee decision is recorded on this deal.</p>
  const when = `${decision.date ?? 'Decision date not recorded'} · committee decision record`
  if (full) {
    // The Credit & Conditions tab has the room to say the decision whole.
    return (
      <div>
        <div className="flex flex-wrap items-center gap-2 text-[10px]">
          <span className="rounded border border-amber-300/30 bg-amber-300/[0.08] px-1.5 py-0.5 font-medium text-amber-200">
            {decision.headline}
          </span>
          {decision.body ? <span className="text-stone-400">{decision.body}</span> : null}
        </div>
        <div className="mt-1.5 text-[9px] text-stone-600">{when}</div>
      </div>
    )
  }
  const note = [decision.body, when].filter(Boolean).join(' · ')
  return (
    <div className="flex min-w-0 items-center gap-2 text-[10px]">
      <span className="shrink-0 rounded border border-amber-300/30 bg-amber-300/[0.08] px-1.5 py-0.5 font-medium text-amber-200">
        {decision.headline}
      </span>
      <span className="truncate text-[9px] text-stone-500" title={note}>{note}</span>
    </div>
  )
}

/** What a risk line says: the risk, and its mitigant when the memo states one. */
function riskText(item: { risk: string; mitigation?: string | null }): string {
  const mitigation = item.mitigation?.trim()
  return mitigation && mitigation !== '—' ? `${item.risk} — ${mitigation}` : item.risk
}

/** Height of one risk line on the dashboard: fixed, so the lines that fit can be counted. */
const RISK_LINE_PX = 15

/**
 * How many fixed-height lines fit in an element, measured as it resizes. Null
 * until measured — and where there is no ResizeObserver, as in a test — which
 * means "show every line". The element must take its height from its panel,
 * not from its content. A callback ref, so a list that mounts only once its
 * data lands is still measured.
 */
function useLinesThatFit<T extends HTMLElement>(lineHeight: number) {
  const [element, setElement] = useState<T | null>(null)
  const [fit, setFit] = useState<number | null>(null)
  useEffect(() => {
    if (!element || typeof ResizeObserver === 'undefined') return
    const observer = new ResizeObserver((entries) =>
      setFit(Math.max(1, Math.floor((entries[0]?.contentRect.height ?? 0) / lineHeight))),
    )
    observer.observe(element)
    return () => observer.disconnect()
  }, [element, lineHeight])
  return [setElement, fit] as const
}

/**
 * The dashboard's risks and conditions: the counts, then the open items one
 * line each — as many as the panel holds. At 1366x768 the panel is about 124px
 * tall, three lines; a scroll bar in a box that small hid the list's length,
 * so the lines that do not fit are counted on the last line instead, and that
 * line opens Credit & Conditions, which has each in full with its owner and
 * due date (the one-screen rule: what does not fit goes to another tab).
 */
function RisksSummary({ model, onMore }: { model: DossierWorkspaceModel; onMore?: () => void }) {
  const [listRef, fit] = useLinesThatFit<HTMLUListElement>(RISK_LINE_PX)
  const memoRisks = model.memoRisks ?? []
  const open = model.conditions.filter((condition) => condition.status !== 'cleared')
  const exceptions = model.exceptions ?? []
  const items = [
    ...memoRisks.map((item) => ({ key: `risk-${item.risk}`, tone: 'text-amber-300', text: riskText(item) })),
    ...open.map((condition, index) => ({
      key: `condition-${condition.label}-${index}`,
      tone: condition.status === 'overdue' ? 'text-red-300' : 'text-sky-300',
      text: `${condition.label}${condition.due ? ` · due ${condition.due}` : ''}`,
    })),
    ...exceptions.map((item, index) => ({ key: `exception-${item.label}-${index}`, tone: 'text-red-300', text: item.label })),
  ]
  // Every line when they all fit; otherwise one line fewer, and a last line
  // saying how many more there are.
  const shown = fit === null || items.length <= fit ? items : items.slice(0, Math.max(fit - 1, 0))
  const more = items.length - shown.length
  return (
    <div className="flex h-full min-h-0 flex-col gap-1.5">
      <div className="grid shrink-0 grid-cols-3 gap-1 text-center">
        {([
          ['Memo risks', memoRisks.length],
          ['Open conditions', open.length],
          ['Exceptions', exceptions.length],
        ] as const).map(([label, count]) => (
          <div key={label} className="rounded border border-stone-800/80 bg-black/25 px-1 py-0.5">
            <div className="font-mono text-[12px] text-stone-100">{count}</div>
            <div className="truncate text-[8px] uppercase tracking-wide text-stone-600">{label}</div>
          </div>
        ))}
      </div>
      {items.length ? (
        <ul ref={listRef} className="min-h-0 flex-1 overflow-hidden text-[9.5px] text-stone-300" data-testid="risk-lines">
          {shown.map((item) => (
            <li key={item.key} className="flex min-w-0 items-center gap-1.5" style={{ height: RISK_LINE_PX }} title={item.text}>
              <AlertTriangle size={10} className={`shrink-0 ${item.tone}`} aria-hidden />
              <span className="truncate">{item.text}</span>
            </li>
          ))}
          {more > 0 ? (
            <li className="flex min-w-0 items-center" style={{ height: RISK_LINE_PX }}>
              <button
                type="button"
                onClick={onMore}
                className="truncate text-amber-300 transition-colors hover:text-amber-200"
              >
                + {more.toLocaleString()} more on Credit &amp; Conditions
              </button>
            </li>
          ) : null}
        </ul>
      ) : (
        <p className="text-[9.5px] text-stone-500">No memo risks, open conditions or exceptions are recorded on this deal.</p>
      )}
    </div>
  )
}

function LatestReview({ review, onOpen }: { review: UnderwritingWorkbookVersion | null; onOpen: () => void }) {
  if (!review) return <EmptyState>No workbook review decision has been recorded.</EmptyState>
  return (
    <div className="space-y-2 text-[9.5px]">
      <div className="flex items-start gap-2">
        <ShieldCheck size={16} className="mt-0.5 shrink-0 text-sky-300" />
        <div className="min-w-0">
          <div className="truncate font-medium text-stone-200">{review.reviewedBy ?? 'Reviewer not recorded'}</div>
          <div className="text-stone-600">{humanDate(review.reviewedAt)}</div>
        </div>
      </div>
      <p className="line-clamp-4 leading-relaxed text-stone-400">{review.reviewNote || `Reviewed ${review.filename}.`}</p>
      <PanelLink label="Open workbook evidence" onClick={onOpen} />
    </div>
  )
}

function KeyFactors({ model }: { model: DossierWorkspaceModel }) {
  return (
    <div className="space-y-2.5">
      <div>
        <div className="mb-1 text-[9px] font-medium text-emerald-300/90">Strengths</div>
        {(model.strengths?.length ?? 0) === 0 ? (
          <p className="text-[9.5px] text-stone-600">No strengths recorded on the credit memo.</p>
        ) : (
          <ul className="space-y-1 text-[9.5px] text-stone-300">
            {(model.strengths ?? []).map((item) => (
              <li key={item} className="flex gap-1.5">
                <CheckCircle2 size={11} className="mt-0.5 shrink-0 text-emerald-400" />
                <span>{item}</span>
              </li>
            ))}
          </ul>
        )}
      </div>
      <div>
        <div className="mb-1 text-[9px] font-medium text-amber-300/90">Considerations</div>
        {(model.memoRisks?.length ?? 0) === 0 ? (
          <p className="text-[9.5px] text-stone-600">No risks recorded on the credit memo.</p>
        ) : (
          <ul className="space-y-1 text-[9.5px] text-stone-300">
            {(model.memoRisks ?? []).map((item) => (
              <li key={item.risk} className="flex gap-1.5">
                <AlertTriangle size={11} className="mt-0.5 shrink-0 text-amber-300" />
                <span>
                  {item.risk}
                  {item.mitigation && item.mitigation.trim() !== '—' ? (
                    <span className="text-stone-500"> — {item.mitigation}</span>
                  ) : null}
                </span>
              </li>
            ))}
          </ul>
        )}
      </div>
    </div>
  )
}

function ConditionsTable({ conditions }: { conditions: DossierWorkspaceModel['conditions'] }) {
  if (!conditions.length) return <EmptyState>No underwriting conditions are recorded on this deal.</EmptyState>
  return (
    <table className="w-full table-fixed text-left text-[9.5px]">
      <thead className="sticky top-0 bg-[#0d0d0d] font-mono text-[7.5px] uppercase tracking-wider text-stone-600">
        <tr>
          <th className="w-[46%] pb-1 font-normal">Condition</th>
          <th className="pb-1 font-normal">Owner</th>
          <th className="pb-1 font-normal">Status</th>
          <th className="pb-1 text-right font-normal">Due</th>
        </tr>
      </thead>
      <tbody className="divide-y divide-stone-900">
        {conditions.map((condition, index) => (
          <tr key={`${condition.label}-${index}`}>
            <td className="truncate py-1.5 pr-2 text-stone-300" title={condition.label}>{condition.label}</td>
            <td className="truncate py-1.5 pr-2 text-stone-600">{condition.owner ?? 'Unassigned'}</td>
            <td className="py-1.5">
              <span className={`rounded border px-1 py-0.5 text-[7.5px] uppercase ${
                condition.status === 'cleared'
                  ? 'border-emerald-500/30 text-emerald-300'
                  : condition.status === 'overdue'
                    ? 'border-red-500/30 text-red-300'
                    : 'border-amber-500/30 text-amber-300'
              }`}>{condition.status}</span>
            </td>
            <td className="py-1.5 text-right font-mono text-stone-500">{condition.due ?? '—'}</td>
          </tr>
        ))}
      </tbody>
    </table>
  )
}

function AttachmentsTable({ documents }: { documents: DealDocument[] }) {
  if (!documents.length) {
    return <EmptyState icon={<FileText size={16} className="text-stone-700" />}>No deal attachments or reports are on record.</EmptyState>
  }
  return (
    <table className="w-full table-fixed text-left text-[9.5px]">
      <thead className="sticky top-0 bg-[#0d0d0d] font-mono text-[7.5px] uppercase tracking-wider text-stone-600">
        <tr>
          <th className="w-[20%] pb-1 font-normal">Type</th>
          <th className="w-[42%] pb-1 font-normal">Name</th>
          <th className="pb-1 font-normal">Uploaded by</th>
          <th className="pb-1 text-right font-normal">Date</th>
        </tr>
      </thead>
      <tbody className="divide-y divide-stone-900">
        {documents.map((document) => (
          <tr key={document.id}>
            <td className="py-1.5 pr-2">
              <span className="flex min-w-0 items-center gap-1 text-stone-400">
                <FileText size={10} className="shrink-0 text-amber-300" />
                <span className="truncate capitalize">{document.category}</span>
              </span>
            </td>
            <td className="truncate py-1.5 pr-2 text-stone-300" title={document.filename}>{document.filename}</td>
            <td className="truncate py-1.5 pr-2 text-stone-600">
              {receivedByEmail(document) ? 'Received by email' : document.uploadedBy ?? 'Uploader not recorded'}
            </td>
            <td className="py-1.5 text-right text-stone-600">{humanDate(document.createdAt)}</td>
          </tr>
        ))}
      </tbody>
    </table>
  )
}

function ActivityList({ activity }: { activity: LoanActivityEvent[] }) {
  if (!activity.length) return <EmptyState>No underwriting activity has been recorded for this deal.</EmptyState>
  return (
    <ul className="divide-y divide-stone-900">
      {activity.map((event) => (
        <li key={event.id} className="flex min-w-0 gap-2 py-1.5">
          <Activity size={11} className="mt-0.5 shrink-0 text-amber-300" />
          <div className="min-w-0 flex-1 text-[9.5px]">
            <div className="flex items-baseline justify-between gap-2">
              <span className="truncate text-stone-300">{statusLabel(event.eventType)}</span>
              <span className="shrink-0 text-[8.5px] text-stone-600">{humanDate(event.createdAt)}</span>
            </div>
            <div className="truncate text-stone-600" title={activitySummary(event) ?? undefined}>
              {activitySummary(event) ?? event.userEmail ?? 'System activity'}
            </div>
          </div>
        </li>
      ))}
    </ul>
  )
}

/**
 * The deal's headline metrics. Unlocked, every one the deal record can store
 * is drawn by EditableSlot — the box every other tab edits through, with its
 * rule that a stored derivation is an output and never an input (R1) — so the
 * figures here are as editable as the same figures on Overview. A metric with
 * no catalogue field (the credit decision, the blocker count) or one Save
 * cannot write reads as it does locked.
 */
function KeyDealMetrics({ model }: { model: DossierWorkspaceModel }) {
  const { unlocked, defs } = useDossierEdit()
  return (
    <div className="grid grid-cols-2 gap-x-3 gap-y-2 sm:grid-cols-4">
      {model.kpis.slice(0, 16).map((metric) => {
        const field = metric.fieldName
        if (unlocked && field && isWritableField(field, defs)) {
          return (
            <div key={metric.id} className="min-w-0 border-b border-stone-900 pb-1.5" data-metric={metric.id}>
              <EditableSlot
                name={field}
                label={metric.label}
                display={metric.value}
                kind={defs.find((def) => def.name === field)?.kind ?? 'text'}
                layout="header"
              />
            </div>
          )
        }
        return (
          <div key={metric.id} className="min-w-0 border-b border-stone-900 pb-1.5" data-metric={metric.id}>
            <div className="truncate font-mono text-[8px] uppercase tracking-wide text-stone-600" title={metric.label}>{metric.label}</div>
            <div className="mt-0.5 flex min-w-0 items-center gap-1">
              <span className="truncate text-[11px] font-medium text-stone-200" title={metric.value}>{metric.value}</span>
              {/* The figure's state and whose it is, on the tile — a derived
                  debt yield or LTC read as plain fact here (UW-04). */}
              {metric.value !== '—' ? (
                <>
                  <ValueStateChip source={metric.source} />
                  <AttributionBadge
                    attribution={metric.source?.attribution}
                    cellRef={metric.source?.cellRef}
                    rollup={metric.source?.rollup}
                  />
                </>
              ) : null}
            </div>
            {metric.source?.label && metric.value !== '—' ? (
              <div className="truncate text-[8px] text-stone-600" title={metric.source.label}>{metric.source.label}</div>
            ) : metric.sublabel ? (
              <div className="truncate text-[8px] text-stone-600">{metric.sublabel}</div>
            ) : null}
          </div>
        )
      })}
    </div>
  )
}

/** What each side of a single-property deal's pair is called, in a sentence. */
const SIDE_WORD: Record<'underwriter' | 'sponsor', string> = {
  underwriter: 'Internal Underwriting',
  sponsor: 'Sponsor',
}

/** What a deal with no name of its own calls the property it describes (the server's word too). */
const FIRST_PROPERTY = 'Property 1'

/** A model in one of these is approved evidence (the server also counts a governing decision). */
const APPROVED_STATUSES = new Set(['reviewed', 'promoted', 'superseded'])

function andList(names: string[]): string {
  return names.length <= 1 ? names.join('') : `${names.slice(0, -1).join(', ')} and ${names[names.length - 1]}`
}

/** A property added from a card's "Add UW Model", with its model. */
type AddPropertyModel = {
  loanId: string
  role: 'underwriter' | 'sponsor'
  property: NewPropertyInput
  model: ChosenModel
}

/**
 * The property was added (so the deal has changed, and may now be a portfolio) but the model
 * meant for it was not filed. Not a refusal of the whole request: the page says both.
 */
class ModelNotFiled extends Error {
  readonly added: AddedProperties
  readonly reason: unknown

  constructor(added: AddedProperties, reason: unknown) {
    super('The model was not filed under the new property')
    this.added = added
    this.reason = reason
  }
}

/** What adding a property did, said where the models are: it outlives the card it was asked on. */
type PropertyOutcome = { loanId: string; tone: 'status' | 'alert'; text: string }

function PropertyOutcomeLine({ outcome, onDismiss }: { outcome: PropertyOutcome; onDismiss: () => void }) {
  return (
    <div
      className={`mb-2 flex items-start justify-between gap-2 rounded border px-2 py-1.5 text-[10px] leading-relaxed ${
        outcome.tone === 'alert'
          ? 'border-red-500/25 bg-red-500/[0.04] text-red-300'
          : 'border-emerald-500/25 bg-emerald-500/[0.04] text-emerald-300/90'
      }`}
    >
      <p role={outcome.tone}>{outcome.text}</p>
      <button type="button" onClick={onDismiss} className="shrink-0 text-[9px] text-stone-500 hover:text-stone-300">
        Dismiss
      </button>
    </div>
  )
}

export function UnderwritingPage({ loanId, model, documents, activity, raw, workflow, onGoto, screen }: UnderwritingPageProps) {
  const { unlocked, effectiveValue, draft, defs, isDerivedField, inputsOf } = useDossierEdit()
  const economics = useMemo(
    () =>
      raw
        ? buildEconomicsModel({
            get: (name) => effectiveValue(name),
            raw,
            activity,
            notes: [],
          })
        : null,
    [raw, effectiveValue, activity],
  )
  const queryClient = useQueryClient()
  // A portfolio parent carries a model per property, per side, and the roll-up to
  // the deal. Only a deal record that says so gets that layer: every other deal,
  // including one whose record is not loaded, keeps the model pair it has always
  // had and makes no portfolio request. A property of a portfolio names its parent.
  const isPortfolio = raw?.is_portfolio === true
  const parentDealId = typeof raw?.parent_deal_id === 'string' && raw.parent_deal_id ? raw.parent_deal_id : null
  // The session's own grants (/auth/me): an Entra session has no local profile, and an
  // absent profile is not authority to add anything.
  const sessionPermissions = useSessionPermissions()
  // Adding a property writes the pipeline (a property is a deal of its own), so it is offered
  // to a role that may edit it, on an unlocked dossier, as Upload is. Only on a deal whose
  // record is known: a portfolio's own property takes none (the server refuses it), and an
  // illustrative pipeline row has no record to add one to. Whether the role may is one
  // question and whether the dossier is unlocked another: a card's Add UW Model, like its
  // Upload, is shown to a role that may use it and locked with the dossier.
  const mayEverAddProperty = canEditModule(sessionPermissions, 'pipeline') && Boolean(raw) && !parentDealId
  const mayAddProperty = unlocked && mayEverAddProperty
  const [mode, setMode] = useState<PageMode>('dashboard')
  const [reviewView, setReviewView] = useState<ReviewView>(isPortfolio ? 'rollup' : 'figures')
  const [underwriterId, setUnderwriterId] = useState<string | null>(null)
  const [sponsorId, setSponsorId] = useState<string | null>(null)
  const [uploadErrors, setUploadErrors] = useState<Partial<Record<WorkbookRole, string>>>({})
  const [heldError, setHeldError] = useState<string | null>(null)
  const [governanceReason, setGovernanceReason] = useState('')
  const [governanceError, setGovernanceError] = useState<string | null>(null)
  // Whose figures the "what the model states" panel reads. The sponsor's by
  // default — it is the model a deal almost always has — the internal one on
  // request. Falls back to whichever exists.
  const [focusRole, setFocusRole] = useState<'sponsor' | 'underwriter'>('sponsor')
  const [adoptError, setAdoptError] = useState<string | null>(null)
  // A card's "Add UW Model" line, open on one side of one deal at a time; the step its request
  // is on; and what the last one did, said above the models (the cards are gone once the deal
  // is a portfolio).
  const [addingOn, setAddingOn] = useState<{ loanId: string; role: 'underwriter' | 'sponsor' } | null>(null)
  const [addStep, setAddStep] = useState<'property' | 'model' | null>(null)
  const [propertyOutcome, setPropertyOutcome] = useState<PropertyOutcome | null>(null)

  // A role without the governed model grant is never sent to ask (follow-up
  // 10): the tab reads everything else and says the models are not its to see.
  const { query: versionsQuery, unavailable: modelsUnavailable } = useLoanWorkbooks(loanId)
  // The portfolio's own routes answer under the same grant, so they wait for the
  // list above to say it is held: a role that is refused is asked once, not three times.
  const portfolioReady = isPortfolio && !modelsUnavailable && versionsQuery.isSuccess
  const portfolioRollup = useQuery({
    queryKey: UW_ROLLUP_KEY(loanId),
    queryFn: () => uwPropertyModelsService.rollup(loanId),
    enabled: portfolioReady,
    // The same query the roll-up panels read: shared, and read when stale or after a change.
    staleTime: 30_000,
  })
  const versions = versionsQuery.data ?? []
  const underwriter = versions.find((version) => version.id === underwriterId && workbookRole(version) === 'underwriter')
    ?? versions.find((version) => workbookRole(version) === 'underwriter')
    ?? null
  const sponsor = versions.find((version) => version.id === sponsorId && workbookRole(version) === 'sponsor')
    ?? versions.find((version) => workbookRole(version) === 'sponsor')
    ?? null

  const focused = (focusRole === 'underwriter' ? underwriter : sponsor) ?? sponsor ?? underwriter ?? null

  // What the focused model states about the deal, cell by cell. One model is
  // enough — this is the read the tab was missing: every other panel here
  // showed counts, and the only figures needed BOTH roles assigned.
  const dealViewQuery = useQuery({
    queryKey: ['underwriting-deal-view', focused?.id],
    queryFn: () => underwritingWorkbooksService.dealView(focused!.id),
    enabled: Boolean(focused?.id),
    staleTime: 30_000,
    retry: false,
  })

  const adopt = useMutation({
    mutationFn: (field: string) => underwritingWorkbooksService.adoptFigure(focused!.id, { field }),
    onSuccess: async () => {
      setAdoptError(null)
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: ['underwriting-deal-view', focused?.id] }),
        invalidateLoanQueries(queryClient, loanId),
      ])
    },
    onError: (error) => setAdoptError(errorDetail(error, 'The figure could not be adopted.')),
  })

  // The same cached lineage read the dossier and the Economics tab make (one
  // fetch, one answer): whose each deal value is, the history behind it, and
  // the broker-stated figures kept beside their base fields.
  const lineageQuery = useQuery({
    queryKey: LOAN_QUERY_KEYS.fieldLineage(loanId),
    queryFn: () => loansService.getFieldLineage(loanId),
    enabled: Boolean(loanId),
    retry: false,
  })

  // What the internal (underwriter-role) model states, for the AREP column.
  // The same query the Model Figures view makes when the internal model is in
  // focus, so the two read one answer.
  const internalViewQuery = useQuery({
    queryKey: ['underwriting-deal-view', underwriter?.id],
    queryFn: () => underwritingWorkbooksService.dealView(underwriter!.id),
    enabled: Boolean(underwriter?.id) && !modelsUnavailable,
    staleTime: 30_000,
    retry: false,
  })

  // The client's column is named from the client profile — the same short
  // name the server gives the underwriter side (`side_label`) — so another
  // client's workspace never prints this one's name on its deal books.
  const clientProfile = useClientProfile()
  const clientLabel = clientSideLabel(clientProfile)

  // A portfolio parent holds no internal model of its own — each property's is
  // filed on the property's record — so the client's column reads the roll-up
  // of those models. Without it every row read "Not underwritten" beside a
  // Models panel saying the internal model was on every property.
  const internalRollup = isPortfolio ? portfolioRollup.data?.sides.find((side) => side.role === 'underwriter') ?? null : null
  // While a read that may answer the client's column is still in flight, its
  // gaps are not answers yet: the column says it is reading, not "Not underwritten".
  const arepPending = modelsUnavailable
    ? null
    : versionsQuery.isLoading
      ? 'Reading the underwriting models'
      : isPortfolio && portfolioRollup.isLoading
        ? 'Reading the property models'
        : underwriter && internalViewQuery.isLoading
          ? 'Reading the internal model'
          : null
  // A read that may answer the client's column and FAILED: its gaps are not
  // absences either. Without this every row said "no internal model states it"
  // of a model list or roll-up nobody could read, and nothing said a read failed.
  // (A failed model list also leaves the roll-up unasked, so it comes first.)
  const arepUnread = modelsUnavailable || arepPending
    ? null
    : versionsQuery.isError
      ? 'The underwriting models could not be read'
      : isPortfolio && portfolioRollup.isError
        ? 'The property models could not be read'
        : underwriter && internalViewQuery.isError
          ? 'The internal model could not be read'
          : null

  const comparisonRows = useMemo(
    () =>
      buildSponsorArepComparison({
        dealValue: (name) => effectiveValue(name),
        // What the record saved, for a field being typed over: the Sponsor
        // side keeps the package's figure while the client's entry is unsaved.
        savedValue: (name) => (name in draft ? catalogueValue(raw, defs, name) : effectiveValue(name)),
        draft,
        isDerived: isDerivedField,
        lineage: lineageQuery.data?.fields ?? [],
        defs,
        sponsorModel: raw?.sponsor_model ?? null,
        statedFigures: lineageQuery.data?.statedFigures ?? null,
        internalModel: internalViewQuery.data ?? null,
        internalRollup,
        arepPending,
        arepUnread,
        // What a field the deal derives is made of, for the client's cell to open.
        inputsOf,
        uwGrid: economics?.uwGrid ?? null,
        clientLabel,
      }),
    [effectiveValue, draft, isDerivedField, lineageQuery.data, defs, raw, internalViewQuery.data, internalRollup, arepPending, arepUnread, inputsOf, economics?.uwGrid, clientLabel],
  )
  const comparisonCounts = comparisonSummary(comparisonRows)

  const comparisonQuery = useQuery({
    queryKey: ['underwriting-workbook-comparison', loanId, underwriter?.id, sponsor?.id],
    queryFn: () => underwritingWorkbooksService.comparison(underwriter!.id, sponsor!.id),
    enabled: Boolean(underwriter?.id && sponsor?.id),
    staleTime: 30_000,
    retry: false,
  })

  // A property's models are part of its portfolio's section and roll-up, and neither is read
  // again on every visit: a change made on the property's own page marks them stale too.
  const refreshPortfolio = () =>
    parentDealId
      ? queryClient.invalidateQueries({ queryKey: ['loan-underwriting-workbooks', parentDealId] })
      : undefined

  const upload = useMutation({
    mutationFn: ({ role, file }: { role: 'underwriter' | 'sponsor'; file: File }) =>
      underwritingWorkbooksService.upload(loanId, file, { role }),
    onSuccess: async (version, variables) => {
      setUploadErrors((current) => ({ ...current, [variables.role]: undefined }))
      if (variables.role === 'underwriter') setUnderwriterId(version.id)
      else setSponsorId(version.id)
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: ['loan-underwriting-workbooks', loanId] }),
        refreshPortfolio(),
      ])
    },
    onError: (error, variables) => {
      setUploadErrors((current) => ({
        ...current,
        [variables.role]: errorDetail(error, 'The workbook could not be uploaded.'),
      }))
    },
  })

  // Another property, with its own model on one side. The property first: the first one makes
  // the deal a portfolio, and the server files the deal's own pair under the property the deal
  // already describes. Then the model, through the routes the portfolio's section uses.
  const addPropertyModel = useMutation({
    mutationFn: async ({ loanId: dealId, role, property, model: chosen }: AddPropertyModel) => {
      setAddStep('property')
      // A workbook on the deal chosen for the new property is not filed under the first on the way.
      const added = await portfolioService.addProperty(
        dealId, 'versionId' in chosen ? { ...property, excludeVersionIds: [chosen.versionId] } : property,
      )
      setAddStep('model')
      try {
        if (!added.propertyId) throw new Error('The new property is not on the deal')
        const filed: PropertyModel = 'versionId' in chosen
          ? await uwPropertyModelsService.assign(chosen.versionId, { propertyId: added.propertyId, role })
          : await uwPropertyModelsService.add(dealId, added.propertyId, chosen.file, { role })
        return { added, filed }
      } catch (reason) {
        throw new ModelNotFiled(added, reason)
      }
    },
    onSuccess: async ({ added, filed }, request) => {
      setAddStep(null)
      setAddingOn(null)
      const first = added.properties.find((property) => property.id === added.filed[0]?.propertyId)?.name
      setPropertyOutcome({
        loanId: request.loanId,
        tone: 'status',
        text: [
          `Added ${request.property.name}, with ${filed.displayName} as its ${SIDE_WORD[request.role]} model.`,
          added.converted ? 'The deal is now a portfolio: each property carries its own models, and they roll up to the deal.' : '',
          first
            ? `${andList(added.filed.map((item) => item.filename))} ${added.filed.length === 1 ? 'is' : 'are'} now filed under ${first}.`
            : '',
          added.notice ?? '',
        ].filter(Boolean).join(' '),
      })
      await refreshAfterPropertyAdded(queryClient, request.loanId)
    },
    onError: async (error, request) => {
      setAddStep(null)
      // Refused outright: nothing changed, and the line that asked says why.
      if (!(error instanceof ModelNotFiled)) return
      setAddingOn(null)
      setPropertyOutcome({
        loanId: request.loanId,
        tone: 'alert',
        text: [
          `Added ${request.property.name}, but its model was not filed: ${errorDetail(error.reason, 'the workbook could not be filed').replace(/\.$/, '')}.`,
          `Add it from ${request.property.name}’s tab.`,
          error.added.notice ?? '',
        ].filter(Boolean).join(' '),
      })
      await refreshAfterPropertyAdded(queryClient, request.loanId)
    },
  })

  /**
   * Sweep an unmapped workbook for anchors.
   *
   * The result is a DRAFT map — it governs nothing until it is approved and
   * locked in the template registry. Before this the review gate asked for a
   * locked template and there was no proposal to take through it, so a sponsor
   * model the engine could read perfectly well sat at "Mapped 0 / 182" with
   * nothing an operator could do about it.
   */
  const proposeMapping = useMutation({
    mutationFn: (versionId: string) => underwritingWorkbooksService.proposeMapping(versionId),
    onSuccess: async () => {
      setUploadErrors((current) => ({ ...current, sponsor: undefined, underwriter: undefined }))
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: ['loan-underwriting-workbooks', loanId] }),
        refreshPortfolio(),
      ])
    },
    onError: (error) => {
      setUploadErrors((current) => ({
        ...current,
        sponsor: errorDetail(error, 'The model could not be swept for a cell map.'),
      }))
    },
  })

  /**
   * The metrics the sweep saw and would not place.
   *
   * Swept fresh rather than stored, so it cannot drift from what the proposal
   * saw. Only asked once a sponsor model exists to ask about.
   */
  const heldMetrics = useQuery({
    queryKey: ['underwriting-held-metrics', sponsor?.id],
    queryFn: () => underwritingWorkbooksService.heldMetrics(sponsor!.id),
    enabled: Boolean(sponsor?.id && sponsor?.templateId),
    staleTime: 60_000,
  })

  // Model Review's views. The cash-flow grid and the held metrics are views of
  // their own only where the deal has one to show; each used to be one more
  // panel stacked under Model Figures, below the fold.
  const hasHeldMetrics = (heldMetrics.data?.length ?? 0) > 0
  const reviewViews = [
    ...(isPortfolio ? ([['rollup', 'Roll-up', Layers]] as const) : []),
    ['figures', 'Model Figures', Table2],
    ...(economics?.uwGrid ? ([['cashflow', 'Cash Flow Grid', BarChart3]] as const) : []),
    ['assumptions', 'Assumptions', Building2],
    ...(hasHeldMetrics ? ([['held', 'Held Metrics', AlertTriangle]] as const) : []),
    ['comparison', 'Comparison', GitCompareArrows],
    ['workbooks', 'Workbook Evidence', FileSpreadsheet],
    ['calculations', 'Calculations', BarChart3],
    ...(workflow ? ([['workflow', 'Workflow', CheckCircle2]] as const) : []),
  ] as readonly (readonly [ReviewView, string, typeof GitCompareArrows])[]
  // A view the deal has no tab for falls back to Model Figures: the roll-up is a portfolio's own
  // view, and the dossier keeps this page as the deal changes, so going from a portfolio to one of
  // its properties would otherwise leave a view with nothing under the tabs.
  const activeView: ReviewView = reviewViews.some(([id]) => id === reviewView) ? reviewView : 'figures'

  const resolveHeld = useMutation({
    mutationFn: ({ metric, candidate }: { metric: string; candidate: HeldMetricCandidate }) =>
      underwritingWorkbooksService.resolveHeldMetric(sponsor!.id, metric, {
        sheet: candidate.sheet,
        labelCell: candidate.labelCell,
        valueCell: candidate.valueCell,
      }),
    onSuccess: async () => {
      setHeldError(null)
      // The workbook's counts, the deal's own fields and the remaining held
      // list all moved together.
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: ['underwriting-held-metrics', sponsor?.id] }),
        queryClient.invalidateQueries({ queryKey: ['loan-underwriting-workbooks', loanId] }),
        refreshPortfolio(),
        invalidateLoanQueries(queryClient, loanId),
      ])
    },
    onError: (error) => setHeldError(errorDetail(error, 'That cell could not be placed.')),
  })

  const proposingRole: 'underwriter' | 'sponsor' | null = proposeMapping.isPending
    ? proposeMapping.variables === underwriter?.id
      ? 'underwriter'
      : 'sponsor'
    : null

  const assignContext = useMutation({
    mutationFn: ({ versionId, role }: { versionId: string; role: 'underwriter' | 'sponsor' }) =>
      underwritingWorkbooksService.setContext(versionId, { role }),
    onSuccess: (updated, variables) => {
      queryClient.setQueryData<UnderwritingWorkbookVersion[]>(['loan-underwriting-workbooks', loanId], (current) =>
        (current ?? []).map((version) => version.id === updated.id ? updated : version),
      )
      if (variables.role === 'underwriter') setUnderwriterId(updated.id)
      else setSponsorId(updated.id)
      queryClient.invalidateQueries({ queryKey: ['underwriting-workbook-comparison', loanId] })
      refreshPortfolio()
    },
    onError: (error, variables) => {
      setUploadErrors((current) => ({
        ...current,
        [variables.role]: errorDetail(error, 'The workbook role could not be assigned.'),
      }))
    },
  })

  const governing = useMutation({
    mutationFn: ({ row, candidate }: { row: UnderwritingComparisonRow; candidate: ComparisonFieldCandidate }) =>
      underwritingWorkbooksService.setGoverningDecision({
        underwriterVersionId: underwriter!.id,
        sponsorVersionId: sponsor!.id,
        semanticKey: row.semanticKey,
        governingVersionId: candidate.provenance.versionId,
        governingFieldId: candidate.fieldId,
        reason: governanceReason.trim(),
      }),
    onSuccess: async () => {
      setGovernanceError(null)
      setGovernanceReason('')
      await queryClient.invalidateQueries({ queryKey: ['underwriting-workbook-comparison', loanId, underwriter?.id, sponsor?.id] })
    },
    onError: (error) => setGovernanceError(errorDetail(error, 'The governing-value decision could not be saved.')),
  })

  const handleSelect = (role: 'underwriter' | 'sponsor', id: string) => {
    const version = versions.find((item) => item.id === id)
    if (!version) return
    setUploadErrors((current) => ({ ...current, [role]: undefined }))
    if (workbookRole(version) === role) {
      if (role === 'underwriter') setUnderwriterId(id)
      else setSponsorId(id)
      return
    }
    if (!unlocked) {
      setUploadErrors((current) => ({ ...current, [role]: 'Unlock the dossier to assign this workbook role.' }))
      return
    }
    assignContext.mutate({ versionId: id, role })
  }

  const handleUpload = (role: 'underwriter' | 'sponsor', file: File) => {
    if (file.size > MAX_WORKBOOK_UPLOAD_BYTES) {
      setUploadErrors((current) => ({
        ...current,
        [role]: `Workbook exceeds the ${MAX_WORKBOOK_UPLOAD_BYTES / 1024 / 1024} MB upload limit.`,
      }))
      return
    }
    upload.mutate({ role, file })
  }

  const openReview = (view: ReviewView) => {
    setReviewView(view)
    setMode('review')
  }

  // A single-property deal's "Add UW Model", one per side. Every model on the deal with a side,
  // revisions included, goes to the property the deal already describes (the server's rule), so
  // a superseded revision never fills the portfolio's record beside the roll-up. The new property
  // is offered any workbook on the deal for its side, or one to upload; the one chosen is left out
  // of what goes to the first property.
  const ownModels = versions.filter((version) => {
    const side = workbookRole(version)
    return side === 'underwriter' || side === 'sponsor'
  })
  const firstProperty =
    typeof raw?.property_name === 'string' && raw.property_name.trim() ? raw.property_name.trim() : FIRST_PROPERTY
  // Approved evidence is an approver's to file, so the server leaves it on the deal (and says so).
  const moving = ownModels.filter((version) => !APPROVED_STATUSES.has(version.status))
  const staying = ownModels.filter((version) => APPROVED_STATUSES.has(version.status))
  const addPropertyNote = [
    `This makes the deal a portfolio: “${firstProperty}” becomes its first property, and this one its second.`,
    moving.length
      ? `The deal’s own ${moving.length === 1 ? 'model' : 'models'} (${andList(moving.map((version) => version.filename))}) ${moving.length === 1 ? 'moves' : 'move'} to “${firstProperty}”, with what ${moving.length === 1 ? 'it' : 'they'} filled on the deal’s record, except a workbook chosen here for the new property.`
      : '',
    staying.length
      ? `${andList(staying.map((version) => version.filename))} ${staying.length === 1 ? 'is' : 'are'} approved and ${staying.length === 1 ? 'stays' : 'stay'} on the deal for an approver to file.`
      : '',
    'Each property’s models roll up to the deal.',
  ].filter(Boolean).join(' ')
  const addModelFor = (role: 'underwriter' | 'sponsor'): AddModelControl => {
    const request = addPropertyModel.variables
    const mine = request?.loanId === loanId && request.role === role
    const refused = addPropertyModel.error instanceof ModelNotFiled ? null : addPropertyModel.error
    return {
      models: versions.filter(
        (version) =>
          workbookRole(version) === role || workbookRole(version) === 'other',
      ),
      note: addPropertyNote,
      // Closed while the dossier is locked, whatever was open before it was.
      open: unlocked && addingOn?.loanId === loanId && addingOn.role === role,
      busy: addPropertyModel.isPending && mine,
      blocked: addPropertyModel.isPending,
      progress:
        addStep === 'property'
          ? 'Adding the property…'
          : addStep === 'model'
            ? request && 'file' in request.model ? 'Parsing the workbook…' : 'Filing the model…'
            : null,
      error: mine && refused ? errorDetail(refused, 'The property could not be added.') : null,
      onOpen: () => {
        addPropertyModel.reset()
        setAddingOn({ loanId, role })
      },
      onClose: () => {
        if (!addPropertyModel.isPending) addPropertyModel.reset()
        setAddingOn(null)
      },
      onSubmit: ({ property, model: chosen }) => {
        if (chosen) addPropertyModel.mutate({ loanId, role, property, model: chosen })
      },
    }
  }
  // The line files a model as well as adding a property, which the models grant governs: a role
  // without it would be left with a portfolio and no model on the new property.
  const addModel =
    mayEverAddProperty && !isPortfolio && canEditModule(sessionPermissions, UNDERWRITING_MODELS_RESOURCE) ? addModelFor : undefined
  const outcome = propertyOutcome?.loanId === loanId ? propertyOutcome : null
  const outcomeLine = outcome ? (
    <PropertyOutcomeLine outcome={outcome} onDismiss={() => setPropertyOutcome(null)} />
  ) : null

  // The deal's own pair. A portfolio keeps it for a whole-portfolio workbook and
  // for anything that arrived with the package before it was filed to a property.
  const dealModelPair = (
    <ModelPair
      versions={versions}
      underwriter={underwriter}
      sponsor={sponsor}
      unlocked={unlocked}
      assigning={assignContext.isPending}
      uploadingRole={upload.isPending ? upload.variables?.role ?? null : null}
      onProposeMapping={(versionId) => proposeMapping.mutate(versionId)}
      proposingRole={proposingRole}
      uploadErrors={uploadErrors}
      onSelect={handleSelect}
      onUpload={handleUpload}
      onReview={() => openReview('workbooks')}
      addModel={addModel}
    />
  )
  // A portfolio's model section: a tab per property, a Sponsor and a client-side (AREP)
  // side on each, and + Add UW Model on both. Null for every other deal.
  const portfolioSection = !isPortfolio ? null : portfolioReady ? (
    <PortfolioModelsPanel loanId={loanId} unlocked={unlocked} canAddProperty={mayAddProperty} dealLevel={dealModelPair} />
  ) : (
    <EmptyState>
      {versionsQuery.isLoading ? 'Loading underwriting models…' : 'The portfolio’s underwriting models could not be loaded.'}
    </EmptyState>
  )

  const reviewedVersions = versions
    .filter((version) => version.reviewedAt)
    .sort((a, b) => new Date(b.reviewedAt ?? 0).getTime() - new Date(a.reviewedAt ?? 0).getTime())
  const latestReview = reviewedVersions[0] ?? null
  const comparison = comparisonQuery.data
  const portfolioCoverage = portfolioRollup.data?.sides.map((side) => `${side.label} ${side.coverage.have} of ${side.coverage.of}`)
  const comparisonStatus = modelsUnavailable
    ? 'Models not available to your role'
    : isPortfolio
    ? portfolioCoverage ? `${portfolioCoverage.join(' · ')} properties` : 'Reading the property models'
    : underwriter && sponsor
    ? comparisonQuery.isLoading
      ? 'Comparing models'
      : comparison
        ? `${comparison.summary.total.toLocaleString()} normalized fields`
        : 'Comparison unavailable'
    : 'Assign both model roles'

  return (
    <div className="flex h-full min-h-0 flex-col gap-2 overflow-hidden">
      <div className="flex flex-wrap items-center justify-between gap-2 rounded-lg border border-stone-800 bg-black/25 px-2 py-1.5">
        <div className="flex rounded border border-stone-800 bg-black/40 p-0.5" role="tablist" aria-label="Underwriting workspace mode">
          {([
            ['dashboard', 'Dashboard', LayoutDashboard],
            ['models', 'Models & Checklist', Layers],
            ['review', 'Model Review', GitCompareArrows],
            ['statement', "Sponsor's Operating Statement", FileSpreadsheet],
            ['scenarios', 'Scenarios & Metrics', LineChart],
            ['credit', 'Credit & Conditions', Gavel],
            ['documents', 'Documents & Activity', FolderOpen],
          ] as const).map(([id, label, Icon]) => (
            <button
              key={id}
              type="button"
              role="tab"
              aria-selected={mode === id}
              onClick={() => setMode(id)}
              className={`inline-flex items-center gap-1.5 whitespace-nowrap rounded px-2 py-1 text-[10px] transition-colors ${
                mode === id ? 'bg-amber-400/15 text-amber-200' : 'text-stone-500 hover:text-stone-300'
              }`}
            >
              <Icon size={11} /> {label}
            </button>
          ))}
        </div>
        <div className="flex items-center gap-2 text-[9.5px] text-stone-500">
          {versionsQuery.isLoading ? <Loader2 size={10} className="animate-spin" /> : <ShieldCheck size={10} className="text-amber-300" />}
          {modelsUnavailable || isPortfolio ? null : (
            <>
              <span>{versions.length.toLocaleString()} model version{versions.length === 1 ? '' : 's'}</span>
              <span className="text-stone-800">|</span>
            </>
          )}
          <span>{comparisonStatus}</span>
        </div>
      </div>

      {versionsQuery.isError && !modelsUnavailable ? (
        <div className="rounded border border-red-500/25 bg-red-500/[0.04] px-3 py-2 text-[10px] text-red-300">
          {errorDetail(versionsQuery.error, 'Could not load underwriting models for this deal.')}
        </div>
      ) : null}

      {parentDealId ? (
        <div
          role="note"
          aria-label="Part of a portfolio"
          className="flex flex-wrap items-center justify-between gap-2 rounded-lg border border-amber-400/20 bg-amber-400/[0.04] px-3 py-1.5 text-[10px] text-amber-100/90"
        >
          <span className="flex min-w-0 items-center gap-1.5">
            <Building2 size={11} aria-hidden className="shrink-0 text-amber-300" />
            <span>
              This property is part of portfolio {parentDealId}. Its Sponsor and {clientLabel} models are read by the
              portfolio’s roll-up.
            </span>
          </span>
          <PanelLink
            label="Open portfolio underwriting"
            onClick={() => useDealDossierStore.getState().openDossier(parentDealId, 'underwriting')}
          />
        </div>
      ) : null}

      {mode === 'dashboard' ? (
        // One screen, no page scroll, at 1600x950 and 1366x768. The comparison
        // is the heart and takes two of three columns; the right column holds
        // three panels of equal height — the models' status, the credit
        // decision, the open risks — so the grid's edges line up top and
        // bottom. Everything that used to scroll beneath has its own sub-tab.
        <div className="min-h-0 flex-1 overflow-y-auto xl:overflow-hidden" data-testid="underwriting-dashboard">
          <div className="dossier-underwriting-grid grid grid-cols-1 gap-2 xl:h-full xl:grid-cols-12">
            <Surface
              fill
              title={`Sponsor vs ${clientLabel}`}
              className="xl:col-span-8"
              action={
                <span className="font-mono text-[9px] text-stone-500" data-testid="comparison-counts">
                  Sponsor {comparisonCounts.sponsor}/{comparisonCounts.total} · {clientLabel}{' '}
                  {arepPending ? 'reading…' : `${comparisonCounts.arep}/${comparisonCounts.total}`}
                  {arepUnread ? ' · read failed' : ''}
                  {comparisonCounts.differ ? ` · ${comparisonCounts.differ} differ` : ''}
                </span>
              }
            >
              {lineageQuery.isLoading ? (
                <EmptyState icon={<Loader2 size={17} className="animate-spin text-amber-300" />}>Reading whose each figure is…</EmptyState>
              ) : (
                <SponsorArepComparisonView
                  rows={comparisonRows}
                  unlocked={unlocked}
                  clientLabel={clientLabel}
                  notice={
                    [
                      lineageQuery.isError ? 'The field lineage could not be read, so each deal value is shown as the package’s' : null,
                      modelsUnavailable
                        ? 'Model figures are not available to your role'
                        : arepUnread
                          ? `${arepUnread}, so ${clientLabel}’s column shows only what the deal record holds`
                          : null,
                    ].filter(Boolean).join(' · ') || null
                  }
                />
              )}
            </Surface>

            <div className="grid min-w-0 grid-cols-1 gap-2 xl:col-span-4 xl:h-full xl:min-h-0 xl:grid-rows-3">
              <Surface
                fill
                title="Models & Checklist"
                action={<PanelLink label="Manage" onClick={() => setMode('models')} />}
              >
                {modelsUnavailable ? (
                  <p className="line-clamp-4 text-[9.5px] leading-relaxed text-stone-500">{MODELS_NOT_AVAILABLE}</p>
                ) : isPortfolio ? (
                  portfolioRollup.data ? (
                    <div className="space-y-1.5">
                      {portfolioRollup.data.sides.map((side) => (
                        <div key={side.role} className="grid grid-cols-[64px_minmax(0,1fr)] items-center gap-2 text-[10px]">
                          <span className="font-mono text-[8px] uppercase tracking-wider text-stone-500">{side.label}</span>
                          <span className="truncate text-stone-300" title={side.missing.map((gap) => gap.name).join(', ') || undefined}>
                            {side.coverage.have} of {side.coverage.of} properties
                            {side.missing.length ? ` · ${side.missing.map((gap) => gap.name).join(', ')} missing` : ''}
                          </span>
                        </div>
                      ))}
                      <ChecklistStrip items={portfolioChecklistItems(portfolioRollup.data)} />
                    </div>
                  ) : (
                    <EmptyState>{portfolioRollup.isError ? 'The property models could not be read.' : 'Reading the property models…'}</EmptyState>
                  )
                ) : (
                  <div className="space-y-1.5">
                    <ModelStatusLine label="Sponsor" version={sponsor} empty="No sponsor model filed" />
                    <ModelStatusLine label="Internal" version={underwriter} empty="No internal model assigned" />
                    <ChecklistStrip items={reviewChecklistItems(underwriter, sponsor, comparison)} />
                  </div>
                )}
              </Surface>

              <Surface
                fill
                title="Recommendation"
                action={<PanelLink label="Credit review" onClick={() => setMode('credit')} />}
              >
                <DecisionLine decision={model.decision} />
                {/* The credit-box screen's verdict and the rule that decided
                    it, here rather than four clicks down in the workflow. */}
                <ScreeningVerdict screen={screen} />
              </Surface>

              <Surface
                fill
                title="Risks & Conditions"
                action={<PanelLink label="View all" onClick={() => setMode('credit')} />}
              >
                <RisksSummary model={model} onMore={() => setMode('credit')} />
              </Surface>
            </div>
          </div>
        </div>
      ) : mode === 'models' ? (
        // The models and the steps taken on them: the pair (or a portfolio's
        // properties) on the left; what they state, the checklist and the last
        // review on the right, in a two-by-two grid of equal panels.
        <div className="min-h-0 flex-1 overflow-y-auto xl:overflow-hidden" data-testid="underwriting-models">
          <div className="grid grid-cols-1 gap-2 xl:h-full xl:grid-cols-12">
            <Surface
              fill
              title="Underwriting Models"
              className="xl:col-span-6"
              action={modelsUnavailable || isPortfolio ? null : <PanelLink label="Manage models" onClick={() => openReview('workbooks')} />}
            >
              {modelsUnavailable ? (
                <EmptyState>{MODELS_NOT_AVAILABLE}</EmptyState>
              ) : (
                <>
                  {outcomeLine}
                  {portfolioSection ?? dealModelPair}
                </>
              )}
            </Surface>

            <div className="grid min-w-0 grid-cols-1 gap-2 xl:col-span-6 xl:h-full xl:min-h-0 xl:grid-cols-2 xl:grid-rows-2">
              {isPortfolio && !modelsUnavailable ? (
                <Surface fill title="Portfolio Roll-up" className="xl:col-span-2">
                  {portfolioReady ? (
                    <PortfolioRollupPanel loanId={loanId} variant="summary" onOpenFull={() => openReview('rollup')} />
                  ) : null}
                </Surface>
              ) : (
                <>
                  <Surface
                    fill
                    title="Underwriting Summary"
                    action={modelsUnavailable ? null : <PanelLink label="View full report" onClick={() => openReview(isPortfolio ? 'rollup' : 'figures')} />}
                  >
                    {/* The summary is what the models state, and every checklist
                        step is a model step: a role that cannot see models is
                        told so, never asked to assign or upload one. */}
                    {modelsUnavailable ? (
                      <EmptyState>{MODELS_NOT_AVAILABLE}</EmptyState>
                    ) : (
                      <ComparisonSummaryChart
                        emptyText={
                          isPortfolio
                            ? 'This deal’s models are filed per property. The Portfolio Roll-up shows what they state.'
                            : undefined
                        }
                        comparison={comparison}
                        coverage={
                          dealViewQuery.data
                            ? {
                                fieldsStated: dealViewQuery.data.coverage.fieldsStated,
                                fieldsApplicable: dealViewQuery.data.coverage.fieldsApplicable,
                                cellsMapped: dealViewQuery.data.coverage.cellsMapped,
                                filename: dealViewQuery.data.filename,
                              }
                            : null
                        }
                      />
                    )}
                  </Surface>
                  <Surface fill title="Latest Review">
                    <LatestReview review={latestReview} onOpen={() => openReview('workbooks')} />
                  </Surface>
                </>
              )}

              <Surface fill title="Underwriting Checklist" className={isPortfolio && !modelsUnavailable ? '' : 'xl:col-span-2'}>
                {modelsUnavailable ? (
                  <EmptyState>{MODELS_NOT_AVAILABLE}</EmptyState>
                ) : isPortfolio ? (
                  <PortfolioChecklist rollup={portfolioRollup.data} failed={portfolioRollup.isError} />
                ) : (
                  <ReviewChecklist underwriter={underwriter} sponsor={sponsor} comparison={comparison} />
                )}
              </Surface>

              {isPortfolio && !modelsUnavailable ? (
                <Surface fill title="Latest Review">
                  <LatestReview review={latestReview} onOpen={() => openReview('workbooks')} />
                </Surface>
              ) : null}
            </div>
          </div>
        </div>
      ) : mode === 'scenarios' ? (
        <div className="min-h-0 flex-1 overflow-y-auto xl:overflow-hidden" data-testid="underwriting-scenarios">
          <div className="grid grid-cols-1 gap-2 xl:h-full xl:grid-cols-2">
            <Surface
              fill
              title="Scenario Analysis"
              action={onGoto ? <PanelLink label="View full analysis" onClick={() => onGoto('economics')} /> : undefined}
            >
              <ScenarioTable economics={economics} />
            </Surface>
            <Surface fill title="Key Deal Metrics">
              <KeyDealMetrics model={model} />
            </Surface>
          </div>
        </div>
      ) : mode === 'credit' ? (
        <div className="min-h-0 flex-1 overflow-y-auto xl:overflow-hidden" data-testid="underwriting-credit">
          <div className="grid grid-cols-1 gap-2 xl:h-full xl:grid-cols-3">
            <Surface fill title="Credit Decision">
              <DecisionLine decision={model.decision} full />
              <ScreeningVerdict screen={screen} />
            </Surface>
            <Surface fill title="Key Underwriting Factors">
              <KeyFactors model={model} />
            </Surface>
            <Surface
              fill
              title="Underwriting Conditions"
              action={onGoto ? <PanelLink label="View all" onClick={() => onGoto('overview')} /> : undefined}
            >
              <ConditionsTable conditions={model.conditions} />
            </Surface>
          </div>
        </div>
      ) : mode === 'documents' ? (
        <div className="min-h-0 flex-1 overflow-y-auto xl:overflow-hidden" data-testid="underwriting-documents">
          <div className="grid grid-cols-1 gap-2 xl:h-full xl:grid-cols-12">
            <Surface
              fill
              title="Attachments & Reports"
              className="xl:col-span-7"
              action={onGoto ? <PanelLink label="View documents" onClick={() => onGoto('documents')} /> : undefined}
            >
              <AttachmentsTable documents={documents} />
            </Surface>
            <Surface
              fill
              title="Recent Activity"
              className="xl:col-span-5"
              action={onGoto ? <PanelLink label="View all activity" onClick={() => onGoto('audit')} /> : undefined}
            >
              <ActivityList activity={activity} />
            </Surface>
          </div>
        </div>
      ) : mode === 'statement' ? (
        // The sponsor's own operating statement, every line with the cell it
        // was read from. Moved here from Economics: it is model evidence.
        <div className="flex min-h-0 flex-1 flex-col overflow-hidden">
          {economics?.sponsorStatement ? (
            <SponsorStatementPanel statement={economics.sponsorStatement} fill />
          ) : (
            <Surface title="Sponsor's Operating Statement">
              <EmptyState icon={<FileSpreadsheet size={18} className="text-stone-700" />}>
                No sponsor operating statement was read from this deal's package.
              </EmptyState>
            </Surface>
          )}
        </div>
      ) : (
        // Model Review: one strip naming the models in review, the review's
        // own views beneath it, and each view sized to the screen. The model
        // cards (and a portfolio's properties) live on Models & Checklist —
        // they used to sit above every view here and left the view itself a
        // 120px slot at 1366x768.
        <div className="flex min-h-0 flex-1 flex-col gap-2 overflow-hidden" data-testid="underwriting-review">
          <div
            className="flex shrink-0 flex-wrap items-center justify-between gap-x-4 gap-y-1 rounded-lg border border-amber-400/15 bg-black/30 px-3 py-1.5"
            aria-label="Models in review"
            role="group"
          >
            {modelsUnavailable ? (
              <span className="truncate text-[9.5px] text-stone-500">{MODELS_NOT_AVAILABLE}</span>
            ) : isPortfolio ? (
              <span className="flex min-w-0 flex-wrap gap-x-4 text-[10px] text-stone-300">
                {(portfolioRollup.data?.sides ?? []).map((side) => (
                  <span key={side.role}>
                    <span className="mr-1.5 font-mono text-[8px] uppercase tracking-wider text-stone-500">{side.label}</span>
                    {side.coverage.have} of {side.coverage.of} properties
                  </span>
                ))}
              </span>
            ) : (
              <div className="grid min-w-0 flex-1 grid-cols-1 gap-x-6 gap-y-1 lg:grid-cols-2">
                <ModelStatusLine label="Sponsor" version={sponsor} empty="No sponsor model filed" />
                <ModelStatusLine label="Internal" version={underwriter} empty="No internal model assigned" />
              </div>
            )}
            {modelsUnavailable ? null : (
              <PanelLink label={isPortfolio ? 'Properties and models' : 'Assign, upload or map models'} onClick={() => setMode('models')} />
            )}
          </div>

          <div className="flex shrink-0 flex-wrap gap-1 border-b border-stone-800 px-1" role="tablist" aria-label="Model review view">
            {reviewViews.map(([id, label, Icon]) => (
              <button
                key={id}
                type="button"
                role="tab"
                aria-selected={activeView === id}
                onClick={() => setReviewView(id)}
                className={`inline-flex items-center gap-1.5 border-b-2 px-2 py-1.5 text-[9.5px] uppercase tracking-wide ${
                  activeView === id ? 'border-amber-300 text-amber-200' : 'border-transparent text-stone-600 hover:text-stone-300'
                }`}
              >
                <Icon size={10} /> {label}
              </button>
            ))}
          </div>

          {/* Every view fills the screen and scrolls a long list inside its
              own panel, never the page (the one-screen rule, 1600x950 and
              1366x768). The workbook explorer, the calculation workbench and
              the workflow used to keep a page scroll of their own — 3,900px of
              stacked panels through a 309px slot — and now lay out to the slot
              like every other view (`fill`). Below xl the view still scrolls. */}
          <div className="min-h-0 flex-1 overflow-y-auto pr-1 xl:overflow-hidden xl:pr-0" data-testid="review-view">
            {/* The deal's figure for each side, rolled up from the property models. */}
            {activeView === 'rollup' && isPortfolio ? (
              modelsUnavailable ? (
                <EmptyState>{MODELS_NOT_AVAILABLE}</EmptyState>
              ) : portfolioReady ? (
                <Surface fill title="Portfolio Roll-up">
                  <PortfolioRollupPanel loanId={loanId} variant="full" />
                </Surface>
              ) : null
            ) : null}
            {activeView === 'figures' ? (
              <div className="grid gap-2 xl:h-full xl:grid-cols-12">
                <Surface
                  fill
                  title="What the model states"
                  className="xl:col-span-8"
                  action={
                    underwriter && sponsor ? (
                      <div className="flex rounded border border-stone-800 bg-black/40 p-0.5" role="tablist" aria-label="Model in focus">
                        {([['sponsor', 'Sponsor'], ['underwriter', 'Internal']] as const).map(([id, label]) => (
                          <button
                            key={id}
                            type="button"
                            role="tab"
                            aria-selected={focusRole === id}
                            onClick={() => setFocusRole(id)}
                            className={`rounded px-2 py-0.5 text-[9px] ${
                              focusRole === id ? 'bg-amber-400/15 text-amber-200' : 'text-stone-500 hover:text-stone-300'
                            }`}
                          >
                            {label}
                          </button>
                        ))}
                      </div>
                    ) : focused ? (
                      <span className="text-[9px] text-stone-500">
                        {workbookRole(focused) === 'underwriter' ? 'Internal model' : 'Sponsor model'}
                      </span>
                    ) : undefined
                  }
                >
                  <ModelDealView
                    view={dealViewQuery.data}
                    loading={Boolean(focused) && dealViewQuery.isLoading}
                    error={dealViewQuery.isError ? errorDetail(dealViewQuery.error, 'The model could not be read.') : null}
                    unlocked={unlocked}
                    adopting={adopt.isPending ? adopt.variables ?? null : null}
                    adoptError={adoptError}
                    onAdopt={(field) => adopt.mutate(field)}
                    compact
                  />
                </Surface>

                <Surface fill title="Model Evidence" className="xl:col-span-4">
                  <WorkbookEvidence underwriter={underwriter} sponsor={sponsor} comparison={comparison} model={model} />
                </Surface>
              </div>
            ) : null}
            {activeView === 'cashflow' && economics?.uwGrid ? (
              <Surface fill title={cashFlowSummaryTitle(economics)}>
                <div data-testid="uw-cash-flow-grid">
                  <div className="mb-1 truncate font-mono text-[8px] uppercase tracking-widest text-amber-200/60">
                    {economics.uwGrid.workbook} · {economics.uwGrid.sheet}
                  </div>
                  <table className="w-full text-left text-[10px]">
                    <thead className="sticky top-0 bg-[#0d0d0d] font-mono text-[8px] uppercase tracking-wider text-stone-600">
                      <tr>
                        <th className="px-1 py-1 font-normal">Line</th>
                        {economics.uwGrid.columns.map((column) => (
                          <th key={column.id} className="px-1 py-1 text-right font-normal">{column.label}</th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {economics.uwGrid.lines.map((line) => (
                        <tr key={`${line.key ?? line.label}`} className="border-t border-white/[0.04] text-stone-300">
                          <td className={`px-1 py-0.5 ${line.key ? 'text-amber-100/80' : ''}`}>{line.label}</td>
                          {economics.uwGrid!.columns.map((column) => (
                            <td key={column.id} className="px-1 text-right font-mono">
                              {line.values[column.id] == null ? '—' : formatAmount(line.values[column.id])}
                            </td>
                          ))}
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </Surface>
            ) : null}
            {activeView === 'assumptions' ? (
              <Surface fill title="Property Assumptions">
                <AssumptionSetsPanel
                  loanId={loanId}
                  unlocked={unlocked}
                  modelVersionId={focused?.id ?? null}
                  modelFilename={focused?.filename ?? null}
                />
              </Surface>
            ) : null}
            {activeView === 'held' && (heldMetrics.data?.length ?? 0) > 0 ? (
              <Surface fill title="Metrics the model states ambiguously">
                <HeldMetricsPanel
                  items={heldMetrics.data ?? []}
                  unlocked={unlocked}
                  resolving={resolveHeld.isPending ? resolveHeld.variables?.metric ?? null : null}
                  error={heldError}
                  onResolve={(metric, candidate) => resolveHeld.mutate({ metric, candidate })}
                />
              </Surface>
            ) : null}
            {activeView === 'comparison' ? (
              <Surface fill title="Normalized Model Comparison">
                <ComparisonWorkspace
                  comparison={comparison}
                  loading={comparisonQuery.isLoading}
                  error={comparisonQuery.error}
                  reason={governanceReason}
                  onReasonChange={setGovernanceReason}
                  savingSemanticKey={governing.isPending ? governing.variables?.row.semanticKey ?? null : null}
                  saveError={governanceError}
                  onGovern={(row, candidate) => governing.mutate({ row, candidate })}
                />
              </Surface>
            ) : null}
            {activeView === 'workbooks'
              ? modelsUnavailable ? <EmptyState>{MODELS_NOT_AVAILABLE}</EmptyState> : <UnderwritingWorkbookPanel loanId={loanId} fill />
              : null}
            {activeView === 'calculations' ? <CalculationWorkbench loanId={loanId} fill /> : null}
            {activeView === 'workflow' ? workflow : null}
          </div>
        </div>
      )}
    </div>
  )
}
